import React, { useState, useEffect } from 'react';
import { formatIsoDate } from '../../../utils/inputFormatters';
import { User } from '../../../types/auth';
import { Business, UserTierInfo, BusinessDashboardData, Account, DashboardView, JournalEntry, JournalEntryCreateRequest } from '../../../types/accounting';
import { accountingApi } from '../../../services/accountingApi';
import tracker from '../../../utils/tracker';
import FinancialMath from '../../../utils/financialMath';
import { copyJournalEntry, journalEntryToForm } from '../../../utils/journalEntryCopy';
import { buildNavGroups, groupOfView, NavGroupId, NavItem } from './navGroups';
import QuickExpenseForm from '../expenses/QuickExpenseForm';
import RecurringEntriesManager from '../recurring/RecurringEntriesManager';
import BankImportWizard from '../imports/BankImportWizard';
import PeriodManager from '../periods/PeriodManager';
import BankReconciliation from '../reconciliation/BankReconciliation';
import SimpleEntryForm from '../SimpleEntryForm';
import EntryAttachments from '../EntryAttachments';
import BudgetManager from '../budgets/BudgetManager';
import ContactsManager from '../contacts/ContactsManager';
import InvoiceManager from '../invoices/InvoiceManager';
import AgingReport from '../reports/AgingReport';
import SalesTaxReport from '../reports/SalesTaxReport';

interface BusinessDashboardProps {
  business: Business;
  user: User;
  userTier: UserTierInfo | null;
}

const emptyJournalEntryForm = (): JournalEntryCreateRequest => ({
  entry_date: new Date().toISOString().split('T')[0],
  description: '',
  reference: '',
  entry_type: 'general',
  lines: [
    { account_id: 0, description: '', debit_amount: 0, credit_amount: 0 },
    { account_id: 0, description: '', debit_amount: 0, credit_amount: 0 }
  ]
});

const entryActionButton: React.CSSProperties = {padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer'};
const lineCell: React.CSSProperties = {padding: '0.5rem', textAlign: 'left'};
const amountCell: React.CSSProperties = {padding: '0.5rem', textAlign: 'right', fontFamily: 'monospace', borderLeft: '1px solid #e5e7eb'};
const lineInput: React.CSSProperties = {width: '100%', boxSizing: 'border-box', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem'};

const BusinessDashboard: React.FC<BusinessDashboardProps> = ({
  business,
  user,
  userTier
}) => {
  const [dashboardData, setDashboardData] = useState<BusinessDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [expandedEntryId, setExpandedEntryId] = useState<number | null>(null);
  const [activeView, setActiveView] = useState<DashboardView>(business.is_sample ? 'dashboard' : 'add-expense');
  const [openGroup, setOpenGroup] = useState<NavGroupId>('enter');
  const navGroups = buildNavGroups(Boolean(userTier?.limits.can_export_reports || business.is_sample), Boolean(userTier?.limits.can_invite_users));

  // The menu opens on the group that holds the current screen
  useEffect(() => {
    const group = groupOfView(navGroups, activeView);
    if (group) setOpenGroup(group);
  }, [activeView]);
  const [accounts, setAccounts] = useState<Record<string, Account[]> | null>(null);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [showAccountCreation, setShowAccountCreation] = useState(false);
  const [accountTemplates, setAccountTemplates] = useState<Record<string, any[]> | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [trialBalance, setTrialBalance] = useState<any>(null);
  const [trialBalanceLoading, setTrialBalanceLoading] = useState(false);
  const [balanceSheet, setBalanceSheet] = useState<any>(null);
  const [balanceSheetLoading, setBalanceSheetLoading] = useState(false);
  const [incomeStatement, setIncomeStatement] = useState<any>(null);
  const [incomeStatementLoading, setIncomeStatementLoading] = useState(false);
  const [cashFlow, setCashFlow] = useState<any>(null);
  const [cashFlowLoading, setCashFlowLoading] = useState(false);
  const [generalLedger, setGeneralLedger] = useState<any>(null);
  const [generalLedgerLoading, setGeneralLedgerLoading] = useState(false);
  const [glAccountFilter, setGlAccountFilter] = useState<number | undefined>(undefined);
  const [showUserInvitation, setShowUserInvitation] = useState(false);
  const [businessUsers, setBusinessUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Monthly Profit Summary State
  const [monthlyProfitData, setMonthlyProfitData] = useState<{
    revenue: number;
    expenses: number;
    netProfit: number;
  } | null>(null);
  const [monthlyProfitLoading, setMonthlyProfitLoading] = useState(false);

  // Journal Entries Manager State
  const [searchEntries, setSearchEntries] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);

  // Which entry has its attachments panel open (dashboard list and All Entries)
  const [attachmentsEntryId, setAttachmentsEntryId] = useState<number | null>(null);

  // Journal Entry Form State
  const [journalEntryForm, setJournalEntryForm] = useState<JournalEntryCreateRequest>(emptyJournalEntryForm());
  const [createAndPost, setCreateAndPost] = useState(false);
  const [savedJournalEntryId, setSavedJournalEntryId] = useState<number | null>(null);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);

  // User Invitation Form State
  const [invitationForm, setInvitationForm] = useState({
    email: '',
    role: 'user' as 'admin' | 'user',
    message: ''
  });

  // Business Settings Form State
  const [businessSettingsForm, setBusinessSettingsForm] = useState({
    business_name: business.business_name,
    business_type: business.business_type,
    industry: business.industry || '',
    phone: business.phone || '',
    email: business.email || '',
    address: business.address || '',
    city: business.city || '',
    state_province: business.state_province || '',
    country: business.country,
    postal_code: business.postal_code || '',
    tax_registration_number: business.tax_registration_number || '',
    fiscal_year_end: business.fiscal_year_end,
    currency: business.currency
  });

  // Account Creation Form State
  const [accountForm, setAccountForm] = useState({
    account_name: '',
    account_type: 'asset' as const,
    account_number: '',
    description: '',
    opening_balance: 0,
    normal_balance: 'debit' as 'debit' | 'credit',
    selectedTemplate: null as any
  });

  // Add Expense is the landing view for a real business, so the chart of accounts has to be there already
  useEffect(() => {
    loadDashboardData();
    loadAccounts();
  }, [business.id]);

  useEffect(() => {
    if (showAccountCreation && !accountTemplates) {
      loadAccountTemplates();
    }
  }, [showAccountCreation, accountTemplates]);

  useEffect(() => {
    if (activeView === 'trial-balance' && !trialBalance) {
      loadTrialBalance();
    }
  }, [activeView, trialBalance, business.id]);

  useEffect(() => {
    if (activeView === 'balance-sheet' && !balanceSheet) {
      loadBalanceSheet();
    }
  }, [activeView, balanceSheet, business.id]);

  useEffect(() => {
    if (activeView === 'income-statement' && !incomeStatement) {
      loadIncomeStatement();
    }
  }, [activeView, incomeStatement, business.id]);

  useEffect(() => {
    if (activeView === 'cash-flow' && !cashFlow) {
      loadCashFlow();
    }
  }, [activeView, cashFlow, business.id]);

  useEffect(() => {
    if (activeView === 'general-ledger' && !generalLedger) {
      loadGeneralLedger();
    }
  }, [activeView, generalLedger, business.id]);

  useEffect(() => {
    if (activeView === 'journal-entries' && searchEntries.length === 0 && !searchLoading) {
      loadSearchEntries();
    }
  }, [activeView]);

  useEffect(() => {
    if (activeView === 'dashboard' && !monthlyProfitData && !monthlyProfitLoading) {
      loadMonthlyProfitData();
    }
  }, [activeView, monthlyProfitData, business.id]);

  const validateBusinessAccess = async (): Promise<boolean> => {
    try {
      // Verify user has access to this business
      const businessesResponse = await accountingApi.getBusinesses();
      const userBusinesses = businessesResponse.data.businesses;
      const hasAccess = userBusinesses.some((b: Business) => b.id === business.id);
      
      if (!hasAccess) {
        tracker('security_business_access_denied', 'business_dashboard', business.id.toString(), 'unauthorized_access_attempt');
        setError(`You do not have permission to open ${business.business_name}.`);
        return false;
      }
      
      return true;
    } catch (err: any) {
      console.error('Business access validation failed:', err);
      setError(`Couldn't load ${business.business_name}. Check your connection and try again.`);
      return false;
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // First validate business access
      const hasAccess = await validateBusinessAccess();
      if (!hasAccess) return;
      
      const response = await accountingApi.getBusinessDashboard(business.id);
      
      if (response.status === 'success' && response.data?.dashboard) {
        setDashboardData(response.data.dashboard);
      } else {
        setError(response.message || 'Failed to load dashboard data');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      setAccountsLoading(true);
      const response = await accountingApi.getBusinessAccounts(business.id);
      
      if (response.status === 'success' && response.data?.accounts) {
        setAccounts(response.data.accounts);
      } else {
        // Set accounts to empty object to show "no accounts" instead of error
        setAccounts({});
      }
    } catch (err: any) {
      console.error('Failed to load accounts:', err);
      // Set accounts to empty object to show "no accounts" instead of error
      setAccounts({});
    } finally {
      setAccountsLoading(false);
    }
  };

  const loadAccountTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const response = await accountingApi.getAccountTemplates();
      
      if (response.status === 'success' && response.data?.templates) {
        setAccountTemplates(response.data.templates);
      } else {
        setAccountTemplates({});
      }
    } catch (err: any) {
      console.error('Failed to load account templates:', err);
      setAccountTemplates({});
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadTrialBalance = async () => {
    try {
      setTrialBalanceLoading(true);
      const response = await accountingApi.getTrialBalance(business.id);
      
      if (response.status === 'success' && response.data) {
        setTrialBalance(response.data);
      } else {
        setTrialBalance({ trial_balance: [], totals: { total_debits: 0, total_credits: 0, is_balanced: true }, as_of_date: new Date().toISOString().split('T')[0] });
      }
    } catch (err: any) {
      console.error('Failed to load trial balance:', err);
      setTrialBalance({ trial_balance: [], totals: { total_debits: 0, total_credits: 0, is_balanced: true }, as_of_date: new Date().toISOString().split('T')[0] });
    } finally {
      setTrialBalanceLoading(false);
    }
  };

  const loadBalanceSheet = async () => {
    try {
      setBalanceSheetLoading(true);
      const response = await accountingApi.getBalanceSheet(business.id);
      
      if (response.status === 'success' && response.data) {
        setBalanceSheet(response.data);
      } else {
        setBalanceSheet({ 
          balance_sheet: { 
            assets: [], 
            liabilities: [], 
            equity: [], 
            totals: { total_assets: 0, total_liabilities: 0, total_equity: 0, balance_check: true } 
          }, 
          as_of_date: new Date().toISOString().split('T')[0] 
        });
      }
    } catch (err: any) {
      console.error('Failed to load balance sheet:', err);
      setBalanceSheet({ 
        balance_sheet: { 
          assets: [], 
          liabilities: [], 
          equity: [], 
          totals: { total_assets: 0, total_liabilities: 0, total_equity: 0, balance_check: true } 
        }, 
        as_of_date: new Date().toISOString().split('T')[0] 
      });
    } finally {
      setBalanceSheetLoading(false);
    }
  };

  const loadIncomeStatement = async () => {
    try {
      setIncomeStatementLoading(true);
      // Default to current year if no dates specified
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;
      
      const response = await accountingApi.getIncomeStatement(business.id, startDate, endDate);
      
      if (response.status === 'success' && response.data) {
        setIncomeStatement(response.data);
      } else {
        setIncomeStatement({ 
          income_statement: { 
            revenue: [], 
            expenses: [], 
            totals: { total_revenue: 0, total_expenses: 0, net_income: 0 } 
          }, 
          period: `${currentYear}`,
          start_date: startDate,
          end_date: endDate
        });
      }
    } catch (err: any) {
      console.error('Failed to load income statement:', err);
      const currentYear = new Date().getFullYear();
      setIncomeStatement({ 
        income_statement: { 
          revenue: [], 
          expenses: [], 
          totals: { total_revenue: 0, total_expenses: 0, net_income: 0 } 
        }, 
        period: `${currentYear}`,
        start_date: `${currentYear}-01-01`,
        end_date: `${currentYear}-12-31`
      });
    } finally {
      setIncomeStatementLoading(false);
    }
  };

  const loadCashFlow = async () => {
    try {
      setCashFlowLoading(true);
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;

      const response = await accountingApi.getCashFlow(business.id, startDate, endDate);

      if (response.status === 'success' && response.data) {
        setCashFlow(response.data);
      } else {
        setCashFlow({
          cash_flow_statement: {
            operating_activities: { net_income: 0, adjustments: [], total_adjustments: 0, net_cash_from_operating: 0 },
            investing_activities: { items: [], net_cash_from_investing: 0 },
            financing_activities: { items: [], net_cash_from_financing: 0 },
            summary: { net_change_in_cash: 0, opening_cash_balance: 0, closing_cash_balance: 0, reconciliation_check: true }
          },
          period: `${currentYear}`,
          start_date: startDate,
          end_date: endDate
        });
      }
    } catch (err: any) {
      console.error('Failed to load cash flow:', err);
      const currentYear = new Date().getFullYear();
      setCashFlow({
        cash_flow_statement: {
          operating_activities: { net_income: 0, adjustments: [], total_adjustments: 0, net_cash_from_operating: 0 },
          investing_activities: { items: [], net_cash_from_investing: 0 },
          financing_activities: { items: [], net_cash_from_financing: 0 },
          summary: { net_change_in_cash: 0, opening_cash_balance: 0, closing_cash_balance: 0, reconciliation_check: true }
        },
        period: `${currentYear}`,
        start_date: `${currentYear}-01-01`,
        end_date: `${currentYear}-12-31`
      });
    } finally {
      setCashFlowLoading(false);
    }
  };

  const loadMonthlyProfitData = async () => {
    try {
      setMonthlyProfitLoading(true);
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth(); // 0-indexed
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const response = await accountingApi.getIncomeStatement(business.id, startDate, endDate);

      if (response.status === 'success' && response.data?.income_statement?.totals) {
        const totals = response.data.income_statement.totals;
        setMonthlyProfitData({
          revenue: totals.total_revenue || 0,
          expenses: totals.total_expenses || 0,
          netProfit: totals.net_income || 0
        });
      } else {
        setMonthlyProfitData({ revenue: 0, expenses: 0, netProfit: 0 });
      }
    } catch (err: any) {
      console.error('Failed to load monthly profit data:', err);
      setMonthlyProfitData({ revenue: 0, expenses: 0, netProfit: 0 });
    } finally {
      setMonthlyProfitLoading(false);
    }
  };

  const loadGeneralLedger = async (accountId?: number) => {
    try {
      setGeneralLedgerLoading(true);
      const currentYear = new Date().getFullYear();
      const response = await accountingApi.getGeneralLedger(
        business.id,
        accountId,
        `${currentYear}-01-01`,
        `${currentYear}-12-31`
      );
      if (response.status === 'success' && response.data) {
        setGeneralLedger(response.data);
      } else {
        setGeneralLedger({ general_ledger: [], start_date: '', end_date: '' });
      }
    } catch (err: any) {
      console.error('Failed to load general ledger:', err);
      setGeneralLedger({ general_ledger: [], start_date: '', end_date: '' });
    } finally {
      setGeneralLedgerLoading(false);
    }
  };

  // Journal Entry Search & Management
  const loadSearchEntries = async (page = 1) => {
    try {
      setSearchLoading(true);
      const response = await accountingApi.searchJournalEntries(business.id, {
        q: searchQuery, status: searchStatus || undefined,
        start_date: searchStartDate || undefined, end_date: searchEndDate || undefined,
        page, limit: 25
      });
      if (response.status === 'success' && response.data) {
        setSearchEntries(response.data.entries);
        setSearchTotal(response.data.pagination.total);
        setSearchPage(page);
      }
    } catch (err: any) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleVoidEntry = async (entryId: number) => {
    if (!window.confirm('Void this entry? This will reverse all account balances. This action cannot be undone.')) return;
    try {
      const response = await accountingApi.voidJournalEntry(entryId);
      if (response.status === 'success') {
        loadSearchEntries(searchPage);
        loadDashboardData();
      } else {
        alert(response.message || 'Failed to void entry');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to void entry');
    }
  };

  const handleDeleteEntry = async (entryId: number) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      const response = await accountingApi.deleteJournalEntry(entryId);
      if (response.status === 'success') {
        loadSearchEntries(searchPage);
        loadDashboardData();
      } else {
        alert(response.message || 'Failed to delete entry');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to delete entry');
    }
  };

  const handleCopyEntry = (entry: JournalEntry) => {
    setJournalEntryForm(copyJournalEntry(entry));
    setCreateAndPost(false);
    setEditingEntry(null);
    setSavedJournalEntryId(null);
    setActiveView('new-entry');
  };

  // Only a draft can be edited: a posted entry is changed by voiding it and entering it again
  const handleEditEntry = (entry: JournalEntry) => {
    setJournalEntryForm(journalEntryToForm(entry));
    setCreateAndPost(false);
    setEditingEntry(entry);
    setSavedJournalEntryId(null);
    setActiveView('new-entry');
  };

  const cancelEditing = () => {
    setEditingEntry(null);
    setJournalEntryForm(emptyJournalEntryForm());
    setActiveView('journal-entries');
  };

  const toggleAttachments = (entryId: number) => {
    setAttachmentsEntryId(current => (current === entryId ? null : entryId));
  };


  // Journal Entry Form Functions
  const journalEntryTotals = {
    totalDebits: journalEntryForm.lines.reduce((sum, line) => sum + (line.debit_amount || 0), 0),
    totalCredits: journalEntryForm.lines.reduce((sum, line) => sum + (line.credit_amount || 0), 0),
    get isBalanced() {
      return Math.abs(this.totalDebits - this.totalCredits) < 0.01 && this.totalDebits > 0;
    }
  };

  const addJournalEntryLine = () => {
    setJournalEntryForm({
      ...journalEntryForm,
      lines: [...journalEntryForm.lines, { account_id: 0, description: '', debit_amount: 0, credit_amount: 0 }]
    });
  };

  const removeJournalEntryLine = (index: number) => {
    if (journalEntryForm.lines.length > 2) {
      const newLines = journalEntryForm.lines.filter((_, i) => i !== index);
      setJournalEntryForm({ ...journalEntryForm, lines: newLines });
    }
  };

  const updateJournalEntryLine = (index: number, field: string, value: any) => {
    const newLines = [...journalEntryForm.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setJournalEntryForm({ ...journalEntryForm, lines: newLines });
  };

  const handleJournalEntrySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!journalEntryTotals.isBalanced) {
      alert('Journal entry must be balanced (debits = credits)');
      return;
    }

    if (journalEntryForm.lines.length < 2) {
      alert('Journal entry must have at least 2 lines');
      return;
    }

    try {
      setSavedJournalEntryId(null);
      const response = editingEntry
        ? await accountingApi.updateJournalEntry(editingEntry.id, journalEntryForm)
        : await accountingApi.createJournalEntry(business.id, journalEntryForm);

      if (response.status === 'success' && response.data?.entry) {
        const entryId = response.data.entry.id;
        const wasEditing = Boolean(editingEntry);
        setEditingEntry(null);
        
        // If createAndPost is true, post the entry immediately
        if (createAndPost) {
          await handlePostJournalEntry(entryId);
        }
        
        // Reset form
        setJournalEntryForm(emptyJournalEntryForm());
        setCreateAndPost(false);
        
        // Refresh dashboard data
        loadDashboardData();

        // Stay on the form: the entry now exists, so an invoice or receipt can be attached to it
        setSavedJournalEntryId(entryId);
        setActionMessage(wasEditing
          ? (createAndPost ? 'Journal entry updated and posted' : 'Journal entry updated')
          : (createAndPost ? 'Journal entry created and posted' : 'Journal entry created as draft'));
        window.setTimeout(() => setActionMessage(null), 4000);
        if (searchEntries.length > 0) loadSearchEntries(searchPage);
      } else {
        alert(response.message || (editingEntry ? 'Failed to update journal entry' : 'Failed to create journal entry'));
      }
    } catch (err: any) {
      console.error('Failed to create journal entry:', err);
      alert(err.message || 'Failed to create journal entry. Please try again.');
    }
  };

  // Account Creation Functions
  const handleAccountCreation = () => {
    tracker('accounting_add_account_button', 'accounting_dashboard', 'clicked', '');
    setShowAccountCreation(true);
    if (!accountTemplates) {
      loadAccountTemplates();
    }
  };

  const getNextAccountNumber = (accountType: string): string => {
    if (!accounts) return '';
    
    // Standard account numbering ranges
    const accountRanges = {
      asset: { start: 1000, prefix: '1' },
      liability: { start: 2000, prefix: '2' },
      equity: { start: 3000, prefix: '3' },
      revenue: { start: 4000, prefix: '4' },
      expense: { start: 5000, prefix: '5' }
    };
    
    const range = accountRanges[accountType as keyof typeof accountRanges];
    if (!range) return '';
    
    // Get existing account numbers for this type
    const existingNumbers = accounts[accountType]?.map(acc => acc.account_number) || [];
    
    // Find the next available number
    let nextNumber = range.start;
    while (existingNumbers.includes(nextNumber)) {
      nextNumber++;
    }
    
    return nextNumber.toString();
  };

  const handleTemplateSelect = (template: any) => {
    tracker('accounting_template_select', 'account_creation', template.template_name, '');
    setAccountForm({
      ...accountForm,
      selectedTemplate: template,
      account_name: template.template_name,
      account_type: template.account_type,
      account_number: template.default_account_number.toString(),
      description: template.description || '',
      normal_balance: template.normal_balance || 'debit'
    });
  };

  const handleAccountTypeChange = (accountType: string) => {
    const suggestedNumber = getNextAccountNumber(accountType);
    // Determine normal_balance based on account type
    const getNormalBalance = (type: string): 'debit' | 'credit' => {
      if (type === 'asset' || type === 'expense') return 'debit';
      if (type === 'liability' || type === 'equity' || type === 'revenue') return 'credit';
      return 'debit';
    };

    setAccountForm({
      ...accountForm,
      account_type: accountType as any,
      account_number: suggestedNumber,
      normal_balance: getNormalBalance(accountType)
    });
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let response;
      
      if (accountForm.selectedTemplate) {
        // Create from template
        response = await accountingApi.createAccountFromTemplate(
          business.id, 
          accountForm.selectedTemplate.id,
          accountForm.account_name !== accountForm.selectedTemplate.template_name ? accountForm.account_name : undefined
        );
      } else {
        // Create custom account
        response = await accountingApi.createAccount(business.id, {
          account_name: accountForm.account_name,
          account_type: accountForm.account_type,
          account_number: parseInt(accountForm.account_number),
          description: accountForm.description,
          opening_balance: accountForm.opening_balance,
          normal_balance: accountForm.normal_balance
        });
      }
      
      if (response.status === 'success') {
        tracker('accounting_account_created', 'account_creation', accountForm.selectedTemplate ? 'template' : 'custom', accountForm.account_type);
        
        // Reset form
        setAccountForm({
          account_name: '',
          account_type: 'asset',
          account_number: '',
          description: '',
          opening_balance: 0,
          normal_balance: 'debit',
          selectedTemplate: null
        });
        
        // Refresh accounts and dashboard
        loadAccounts();
        loadDashboardData();
        setShowAccountCreation(false);
        
        alert('Account created successfully!');
      } else {
        tracker('accounting_account_create_error', 'account_creation', '', response.message || 'Failed to create account');
        alert(response.message || 'Failed to create account');
      }
    } catch (err: any) {
      console.error('Failed to create account:', err);
      alert(err.message || 'Failed to create account. Please try again.');
    }
  };

  // Journal Entry Posting Function
  const handlePostJournalEntry = async (entryId: number) => {
    try {
      const response = await accountingApi.postJournalEntry(entryId);
      
      if (response.status === 'success') {
        // Refresh dashboard data to show updated entry status
        loadDashboardData();
        // Also refresh trial balance and balance sheet if they're loaded
        if (trialBalance) {
          setTrialBalance(null);
        }
        if (balanceSheet) {
          setBalanceSheet(null);
        }
        
        if (searchEntries.length > 0) loadSearchEntries(searchPage);
        setActionMessage('Journal entry posted');
        window.setTimeout(() => setActionMessage(null), 4000);
      } else {
        alert(response.message || 'Failed to post journal entry');
      }
    } catch (err: any) {
      console.error('Failed to post journal entry:', err);
      alert(err.message || 'Failed to post journal entry. Please try again.');
    }
  };

  // User Invitation Functions
  const loadBusinessUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await accountingApi.getBusinessUsers(business.id);
      
      if (response.status === 'success' && response.data?.users) {
        setBusinessUsers(response.data.users);
      } else {
        setBusinessUsers([]);
      }
    } catch (err: any) {
      console.error('Failed to load business users:', err);
      setBusinessUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserInvitation = () => {
    setShowUserInvitation(true);
    if (businessUsers.length === 0) {
      loadBusinessUsers();
    }
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await accountingApi.inviteUser(business.id, {
        email: invitationForm.email,
        role: invitationForm.role,
        message: invitationForm.message
      });
      
      if (response.status === 'success') {
        // Reset form
        setInvitationForm({
          email: '',
          role: 'user',
          message: ''
        });

        // Refresh users list
        loadBusinessUsers();
        setShowUserInvitation(false);

        alert('Invitation sent successfully!');
      } else {
        alert(response.message || 'Failed to send invitation');
      }
    } catch (err: any) {
      console.error('Failed to send invitation:', err);
      const errorMessage = err.response?.data?.message || 'Failed to send invitation. Please try again.';
      alert(errorMessage);
    }
  };

  // Business Settings Functions
  const handleBusinessSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await accountingApi.updateBusiness(business.id, businessSettingsForm);
      
      if (response.status === 'success') {
        alert('Business settings updated successfully!');
        // The parent component should handle refreshing the business data
      } else {
        alert(response.message || 'Failed to update business settings');
      }
    } catch (err: any) {
      console.error('Failed to update business settings:', err);
      alert('Failed to update business settings. Please try again.');
    }
  };

  // Only the first load takes over the screen: a background refresh must not unmount the form you are filling in
  if (loading && !dashboardData) {
    return (
      <div className="business-dashboard loading">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Loading {business.business_name} dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="business-dashboard error">
        <div className="error-message">
          <h3>⚠️ Error Loading Dashboard</h3>
          <p>{error}</p>
          <button className="btn-primary" onClick={loadDashboardData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: business.currency
    }).format(amount);
  };

  const currentGroup = groupOfView(navGroups, activeView);
  const openItems = navGroups.find(group => group.id === openGroup)?.items || [];
  const openNavItem = (item: NavItem) => {
    if (item.track) tracker(item.track, 'navigation', 'clicked', '');
    if (item.action === 'invite-users') {
      handleUserInvitation();
      return;
    }
    if (item.view === 'general-ledger' && !accounts) loadAccounts();
    if (item.view) setActiveView(item.view);
  };

  return (
    <div className="business-dashboard">
      {actionMessage && (
        <div className="dashboard-action-message" role="status">{actionMessage}</div>
      )}
      <header className="dashboard-header">
        <nav className="nav-groups" aria-label="Accounting sections">
          <button
            type="button"
            className={`nav-group nav-home${activeView === 'dashboard' ? ' open' : ''}`}
            onClick={() => setActiveView('dashboard')}
            aria-current={activeView === 'dashboard' ? 'page' : undefined}
          >
            <span className="card-icon">🏠</span>
            <span>Dashboard</span>
          </button>
          {navGroups.map(group => (
            <button
              key={group.id}
              type="button"
              className={`nav-group${openGroup === group.id ? ' open' : ''}${currentGroup === group.id ? ' current' : ''}`}
              onClick={() => setOpenGroup(group.id)}
              aria-expanded={openGroup === group.id}
            >
              {group.label}
            </button>
          ))}
          <span className="business-meta">
            <span className="business-type">{business.business_type.replace(/_/g, ' ')}</span>
            <span className="fiscal-year">FY ends {business.fiscal_year_end}</span>
          </span>
        </nav>
        <div className="dashboard-actions">
          <div className="nav-items">
            {openItems.map(item => (
              <button
                key={item.label}
                type="button"
                className={`action-card${item.view && activeView === item.view ? ' active' : ''}`}
                onClick={() => openNavItem(item)}
                aria-current={item.view && activeView === item.view ? 'page' : undefined}
              >
                <span className="card-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        {activeView === 'dashboard' && dashboardData && (
          <>
            {/* Monthly Profit Summary */}
            <section style={{marginBottom: '1.5rem'}}>
              <h3 style={{margin: '0 0 0.75rem 0', fontSize: '1.1rem', color: '#374151'}}>
                {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })} so far
              </h3>
              {monthlyProfitLoading ? (
                <p style={{color: '#6b7280'}}>Loading monthly summary...</p>
              ) : monthlyProfitData ? (
                <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
                  {/* Revenue Card */}
                  <div style={{
                    flex: '1 1 200px',
                    padding: '1.25rem',
                    borderRadius: '8px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem'}}>Revenue</div>
                    <div style={{fontSize: '1.5rem', fontWeight: 700, color: '#059669'}}>
                      {formatCurrency(monthlyProfitData.revenue)}
                    </div>
                  </div>
                  {/* Expenses Card */}
                  <div style={{
                    flex: '1 1 200px',
                    padding: '1.25rem',
                    borderRadius: '8px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca'
                  }}>
                    <div style={{fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem'}}>Expenses</div>
                    <div style={{fontSize: '1.5rem', fontWeight: 700, color: '#dc2626'}}>
                      {formatCurrency(monthlyProfitData.expenses)}
                    </div>
                  </div>
                  {/* Net Profit Card */}
                  <div style={{
                    flex: '1 1 200px',
                    padding: '1.25rem',
                    borderRadius: '8px',
                    backgroundColor: monthlyProfitData.netProfit >= 0 ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${monthlyProfitData.netProfit >= 0 ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    <div style={{fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem'}}>Net Profit</div>
                    <div style={{fontSize: '1.5rem', fontWeight: 700, color: monthlyProfitData.netProfit >= 0 ? '#059669' : '#dc2626'}}>
                      {formatCurrency(monthlyProfitData.netProfit)}
                    </div>
                  </div>
                </div>
              ) : null}
            </section>

            {/* Financial Summary */}
            <section className="dashboard-section financial-summary">
              <h3>📊 Financial Overview</h3>
              <div className="summary-cards">
                <div className="summary-card assets">
                  <div className="card-header">
                    <span className="card-icon">💰</span>
                    <span className="card-title">Total Assets</span>
                  </div>
                  <div className="card-value">
                    {formatCurrency(dashboardData.balance_summary.total_assets)}
                  </div>
                </div>

                <div className="summary-card liabilities">
                  <div className="card-header">
                    <span className="card-icon">📋</span>
                    <span className="card-title">Total Liabilities</span>
                  </div>
                  <div className="card-value">
                    {formatCurrency(dashboardData.balance_summary.total_liabilities)}
                  </div>
                </div>

                <div className="summary-card equity">
                  <div className="card-header">
                    <span className="card-icon">🏦</span>
                    <span className="card-title">Total Equity</span>
                  </div>
                  <div className="card-value">
                    {formatCurrency(dashboardData.balance_summary.total_equity)}
                  </div>
                </div>

                <div className={`summary-card balance-check ${dashboardData.balance_summary.balance_check ? 'balanced' : 'unbalanced'}`}>
                  <div className="card-header">
                    <span className="card-icon">
                      {dashboardData.balance_summary.balance_check ? '✅' : '⚠️'}
                    </span>
                    <span className="card-title">Balance Check</span>
                  </div>
                  <div className="card-value">
                    {dashboardData.balance_summary.balance_check ? 'Balanced' : 'Unbalanced'}
                  </div>
                </div>
              </div>
            </section>

            {/* Account Summary */}
            <section className="dashboard-section account-summary">
              <h3>📁 Chart of Accounts</h3>
              <div className="account-types">
                {Object.entries(dashboardData.account_summary).map(([type, count]) => (
                  <div key={type} className="account-type-card">
                    <div className="type-icon">
                      {type === 'asset' && '💰'}
                      {type === 'liability' && '📋'}
                      {type === 'equity' && '🏦'}
                      {type === 'revenue' && '📈'}
                      {type === 'expense' && '💸'}
                    </div>
                    <div className="type-info">
                      <div className="type-name">{{asset:'Assets',liability:'Liabilities',equity:'Equity',revenue:'Revenue',expense:'Expenses'}[type] || (type.charAt(0).toUpperCase() + type.slice(1))}</div>
                      <div className="type-count">{count} accounts</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Recent Journal Entries */}
            <section className="dashboard-section recent-entries">
              <h3>📝 Recent Journal Entries</h3>
              {business.is_sample && dashboardData.recent_entries.some(e => e.status === 'draft') && (
                <p style={{margin: '0 0 0.75rem', color: '#6b7280'}}>
                  Start here: post the draft entry and watch the books update. Click any entry to see its debits and credits.
                </p>
              )}
              {dashboardData.recent_entries.length > 0 ? (
                <div className="entries-list">
                  {dashboardData.recent_entries.map(entry => (
                    <React.Fragment key={entry.id}>
                    <div className="entry-item">
                      <div className="entry-header">
                        <span className="entry-number">{entry.entry_number}</span>
                        <span className="entry-date">{entry.entry_date}</span>
                        <span className={`entry-status status-${entry.status}`}>
                          {entry.status}
                        </span>
                      </div>
                      <div className="entry-description">
                        {entry.description || 'No description'}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '0.75rem',
                        flexShrink: 0
                      }}>
                        <div className="entry-amount">
                          {formatCurrency(entry.total_amount)}
                        </div>
                        {entry.status === 'draft' && (
                          <button
                            onClick={() => handlePostJournalEntry(entry.id)}
                            style={{
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Post Entry
                          </button>
                        )}
                        <button
                          onClick={() => toggleAttachments(entry.id)}
                          title="Invoices, receipts and other support"
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: attachmentsEntryId === entry.id ? '#dbeafe' : '#f3f4f6',
                            color: attachmentsEntryId === entry.id ? '#2563eb' : '#6b7280',
                            border: 'none', borderRadius: '4px', cursor: 'pointer'
                          }}
                        >
                          {String.fromCodePoint(0x1F4CE)}
                        </button>
                      </div>
                    </div>
                    {attachmentsEntryId === entry.id && (
                      <div style={{margin: '-0.25rem 0 0.5rem', padding: '0.75rem 1rem', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px'}}>
                        <EntryAttachments businessId={business.id} entryId={entry.id} />
                      </div>
                    )}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div className="no-entries">
                  <p>No journal entries yet</p>
                  <button className="btn-primary" onClick={() => setActiveView('new-entry')}>
                    Create Your First Entry
                  </button>
                </div>
              )}
            </section>
          </>
        )}

        {activeView === 'add-expense' && accounts && (
          <section className="dashboard-section">
            <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span>💰</span> Add Expense
              <span style={{fontSize: '12px', fontWeight: 'normal', color: '#6b7280', marginLeft: '8px'}}>
                (Quick entry - no accounting knowledge required)
              </span>
            </h3>
            <div style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
              <QuickExpenseForm
                businessId={business.id}
                accounts={accounts}
                currency={business.currency}
                onSuccess={() => {
                  loadDashboardData();
                  loadAccounts();
                }}
                onCancel={() => setActiveView('dashboard')}
              />
            </div>
          </section>
        )}

        {activeView === 'simple-entry' && (
          <section className="dashboard-section">
            <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span>✏️</span> Simple Entry
              <span style={{fontSize: '12px', fontWeight: 'normal', color: '#6b7280', marginLeft: '8px'}}>
                (I spent money / I received money)
              </span>
            </h3>
            <SimpleEntryForm
              businessId={business.id}
              onEntryCreated={() => {
                loadDashboardData();
                loadAccounts();
              }}
            />
          </section>
        )}

        {activeView === 'recurring' && accounts && (
          <section className="dashboard-section">
            <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span>🔄</span> Recurring Entries
              <span style={{fontSize: '12px', fontWeight: 'normal', color: '#6b7280', marginLeft: '8px'}}>
                (Automatic scheduled entries for rent, payroll, etc.)
              </span>
            </h3>
            <div style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
              <RecurringEntriesManager
                businessId={business.id}
                accounts={accounts}
                currency={business.currency}
                onClose={() => setActiveView('dashboard')}
              />
            </div>
          </section>
        )}

        {activeView === 'bank-import' && (
          <section className="dashboard-section">
            <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span>🏦</span> Bank Import
              <span style={{fontSize: '12px', fontWeight: 'normal', color: '#6b7280', marginLeft: '8px'}}>
                (Import transactions from your bank CSV files)
              </span>
            </h3>
            <div style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
              <BankImportWizard
                businessId={business.id}
                accounts={accounts || {}}
                currency={business.currency}
                onComplete={() => {
                  loadDashboardData();
                  loadAccounts();
                  setActiveView('dashboard');
                }}
                onCancel={() => setActiveView('dashboard')}
              />
            </div>
          </section>
        )}

        {activeView === 'new-entry' && (
          <section className="dashboard-section">
            <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span>📝</span> Journal Entry
              <span style={{fontSize: '12px', fontWeight: 'normal', color: '#6b7280', marginLeft: '8px'}}>
                (Advanced - for accountants)
              </span>
            </h3>
            {editingEntry && (
              <div style={{marginBottom: '1rem', padding: '0.75rem 1rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem'}}>
                <span style={{color: '#1e40af'}}>
                  Editing {editingEntry.entry_number}. Posted entries cannot be changed, so only drafts open here.
                </span>
                <button type="button" onClick={cancelEditing}
                  style={{padding: '0.3rem 0.8rem', background: 'white', border: '1px solid #bfdbfe', borderRadius: '6px', color: '#1e40af', cursor: 'pointer'}}>
                  Cancel edit
                </button>
              </div>
            )}
            {savedJournalEntryId && (
              <div style={{marginBottom: '1rem', padding: '1rem', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px'}}>
                <div style={{marginBottom: '0.5rem', color: '#166534', fontWeight: 500}}>
                  Entry saved. Attach the invoice or receipt, or fill the form again for another entry.
                </div>
                <EntryAttachments businessId={business.id} entryId={savedJournalEntryId} />
                <button
                  type="button"
                  onClick={() => { setSavedJournalEntryId(null); setActiveView('dashboard'); }}
                  style={{marginTop: '0.75rem', padding: '0.4rem 1rem', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer'}}
                >
                  Done
                </button>
              </div>
            )}
            <div style={{background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
              <form onSubmit={handleJournalEntrySubmit} style={{maxWidth: '800px'}}>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem'}}>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Entry Date</label>
                    <input
                      type="date"
                      value={journalEntryForm.entry_date}
                      onChange={(e) => setJournalEntryForm({...journalEntryForm, entry_date: e.target.value})}
                      required
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Entry Type</label>
                    <select
                      value={journalEntryForm.entry_type}
                      onChange={(e) => setJournalEntryForm({...journalEntryForm, entry_type: e.target.value as any})}
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    >
                      <option value="general">General</option>
                      <option value="adjustment">Adjustment</option>
                      <option value="closing">Closing</option>
                      <option value="opening">Opening</option>
                    </select>
                  </div>
                </div>
                
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem'}}>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Description</label>
                    <input
                      type="text"
                      value={journalEntryForm.description}
                      onChange={(e) => setJournalEntryForm({...journalEntryForm, description: e.target.value})}
                      placeholder="Enter journal entry description"
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Reference</label>
                    <input
                      type="text"
                      value={journalEntryForm.reference}
                      onChange={(e) => setJournalEntryForm({...journalEntryForm, reference: e.target.value})}
                      placeholder="Invoice #, Cheque #, etc."
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                    <h4 style={{margin: 0}}>Journal Entry Lines</h4>
                    <button
                      type="button"
                      onClick={addJournalEntryLine}
                      style={{background: '#dc2626', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer'}}
                    >
                      ➕ Add Line
                    </button>
                  </div>
                  
                  <div style={{border: '1px solid #e5e7eb', borderRadius: '6px', overflowX: 'auto'}}>
                    <table style={{width: '100%', minWidth: '560px', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '0.875rem'}}>
                      <colgroup>
                        <col style={{width: '34%'}} />
                        <col style={{width: '28%'}} />
                        <col style={{width: '16%'}} />
                        <col style={{width: '16%'}} />
                        <col style={{width: '6%'}} />
                      </colgroup>
                      <thead>
                        <tr style={{background: '#f9fafb', textAlign: 'left'}}>
                          <th style={lineCell}>Account</th>
                          <th style={lineCell}>Description</th>
                          <th style={lineCell}>Debit</th>
                          <th style={lineCell}>Credit</th>
                          <th style={lineCell}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {journalEntryForm.lines.map((line, index) => (
                          <tr key={index} style={{borderTop: '1px solid #e5e7eb'}}>
                            <td style={lineCell}>
                              <select
                                value={line.account_id || ''}
                                onChange={(e) => updateJournalEntryLine(index, 'account_id', parseInt(e.target.value) || 0)}
                                required
                                style={lineInput}
                              >
                                <option value="">Select Account...</option>
                                {accounts && Object.entries(accounts).map(([accountType, accountList]) => (
                                  <optgroup key={accountType} label={accountType.charAt(0).toUpperCase() + accountType.slice(1)}>
                                    {(accountList as any[]).map((account: any) => (
                                      <option key={account.id} value={account.id}>
                                        {account.account_number} - {account.account_name}
                                      </option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                            </td>
                            <td style={lineCell}>
                              <input
                                type="text"
                                value={line.description}
                                onChange={(e) => updateJournalEntryLine(index, 'description', e.target.value)}
                                placeholder="Line description"
                                style={lineInput}
                              />
                            </td>
                            <td style={lineCell}>
                              <input
                                type="number"
                                step="0.01"
                                value={line.debit_amount || ''}
                                onChange={(e) => updateJournalEntryLine(index, 'debit_amount', parseFloat(e.target.value) || 0)}
                                placeholder="0.00"
                                style={lineInput}
                              />
                            </td>
                            <td style={lineCell}>
                              <input
                                type="number"
                                step="0.01"
                                value={line.credit_amount || ''}
                                onChange={(e) => updateJournalEntryLine(index, 'credit_amount', parseFloat(e.target.value) || 0)}
                                placeholder="0.00"
                                style={lineInput}
                              />
                            </td>
                            <td style={{...lineCell, textAlign: 'center'}}>
                              <button
                                type="button"
                                onClick={() => removeJournalEntryLine(index)}
                                title="Remove line"
                                style={{background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '0.4rem 0.6rem'}}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '6px'}}>
                    <div>
                      <strong>Total Debits: ${journalEntryTotals.totalDebits.toFixed(2)}</strong>
                    </div>
                    <div>
                      <strong>Total Credits: ${journalEntryTotals.totalCredits.toFixed(2)}</strong>
                    </div>
                    <div style={{color: journalEntryTotals.isBalanced ? '#10b981' : '#ef4444'}}>
                      <strong>{journalEntryTotals.isBalanced ? '✓ Balanced' : '⚠ Out of Balance'}</strong>
                    </div>
                  </div>
                </div>

                {/* Post Option */}
                <div style={{marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
                    <input
                      type="checkbox"
                      checked={createAndPost}
                      onChange={(e) => setCreateAndPost(e.target.checked)}
                      style={{marginRight: '0.5rem'}}
                    />
                    <span style={{fontWeight: '500'}}>📝 Create and Post Immediately</span>
                  </label>
                  <p style={{margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#6b7280'}}>
                    {createAndPost ? 
                      'Entry will be posted immediately and affect account balances.' : 
                      'Entry will be saved as draft. You can post it later.'
                    }
                  </p>
                </div>

                <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
                  <button
                    type="button"
                    onClick={() => (editingEntry ? cancelEditing() : setActiveView('dashboard'))}
                    style={{padding: '0.75rem 1.5rem', border: '1px solid #d1d5db', background: 'white', borderRadius: '6px', cursor: 'pointer'}}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!journalEntryTotals.isBalanced || journalEntryForm.lines.length < 2}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: journalEntryTotals.isBalanced && journalEntryForm.lines.length >= 2 ? (createAndPost ? '#059669' : '#10b981') : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: journalEntryTotals.isBalanced && journalEntryForm.lines.length >= 2 ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {editingEntry
                      ? (createAndPost ? '📝 Save & Post Changes' : '💾 Save Changes')
                      : (createAndPost ? '📝 Create & Post Entry' : '💾 Save as Draft')}
                  </button>
                </div>
              </form>
            </div>
          </section>
        )}

        {activeView === 'accounts' && (
          <section className="dashboard-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
              <h3>📁 Chart of Accounts</h3>
              <button 
                className="btn-primary" 
                style={{fontSize: '0.875rem'}}
                onClick={handleAccountCreation}
              >
                ➕ Add Account
              </button>
            </div>
            
            {accountsLoading ? (
              <div style={{background: 'white', padding: '3rem', borderRadius: '8px', border: '1px solid #e5e7eb', textAlign: 'center'}}>
                <div className="spinner" style={{margin: '0 auto 1rem'}}></div>
                <p style={{color: '#6b7280'}}>Loading accounts...</p>
              </div>
            ) : accounts ? (
              <div style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden'}}>
                {Object.entries(accounts).map(([accountType, accountList]) => (
                  <div key={accountType} style={{borderBottom: '1px solid #e5e7eb'}}>
                    <div style={{background: '#f9fafb', padding: '1rem', borderBottom: '1px solid #e5e7eb'}}>
                      <h4 style={{margin: 0, color: '#374151', fontSize: '1.1rem', fontWeight: 600}}>
                        {accountType === 'asset' && '💰 Assets'}
                        {accountType === 'liability' && '📋 Liabilities'} 
                        {accountType === 'equity' && '🏦 Equity'}
                        {accountType === 'revenue' && '📈 Revenue'}
                        {accountType === 'expense' && '💸 Expenses'}
                        <span style={{color: '#6b7280', fontWeight: 400, marginLeft: '0.5rem'}}>
                          ({accountList.length} accounts)
                        </span>
                      </h4>
                    </div>
                    
                    <div>
                      {accountList.map((account) => (
                        <div key={account.id} style={{
                          display: 'grid', 
                          gridTemplateColumns: '100px 1fr auto auto', 
                          alignItems: 'center',
                          padding: '0.75rem 1rem',
                          borderBottom: '1px solid #f3f4f6',
                          gap: '1rem'
                        }}>
                          <div style={{fontFamily: 'monospace', color: '#6b7280', fontSize: '0.875rem'}}>
                            {account.account_number}
                          </div>
                          <div>
                            <div style={{fontWeight: 500, color: '#1f2937'}}>
                              {account.account_name}
                            </div>
                            {account.description && (
                              <div style={{fontSize: '0.875rem', color: '#6b7280'}}>
                                {account.description}
                              </div>
                            )}
                          </div>
                          <div style={{textAlign: 'right', fontSize: '0.875rem', color: '#6b7280'}}>
                            {(account.current_balance < 0) === (account.normal_balance === 'debit') ? 'Cr' : 'Dr'}
                          </div>
                          <div style={{textAlign: 'right', fontWeight: 500, fontFamily: 'monospace'}}>
                            {formatCurrency(Math.abs(account.current_balance))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {Object.keys(accounts).length === 0 && (
                  <div style={{padding: '3rem', textAlign: 'center', color: '#6b7280'}}>
                    <p>No accounts found</p>
                    <button 
                      className="btn-primary" 
                      style={{marginTop: '1rem'}}
                      onClick={handleAccountCreation}
                    >
                      Create Your First Account
                    </button>
                  </div>
                )}
              </div>
            ) : accounts && Object.keys(accounts).length > 0 ? (
              <div style={{background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                {Object.entries(accounts).map(([accountType, accountList]) => (
                  <div key={accountType} style={{marginBottom: '1.5rem'}}>
                    <h4 style={{textTransform: 'capitalize', marginBottom: '0.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem'}}>{accountType}</h4>
                    {(accountList as any[]).map((account: any) => (
                      <div key={account.id} style={{display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6'}}>
                        <div>
                          <strong>{account.account_name}</strong>
                          <br />
                          <small style={{color: '#6b7280'}}>{account.account_code}</small>
                        </div>
                        <div style={{textAlign: 'right'}}>
                          <div style={{fontWeight: 'bold'}}>${account.balance?.toFixed(2) || '0.00'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                <p style={{color: '#6b7280', textAlign: 'center', padding: '3rem'}}>
                  📁 No accounts found.<br />
                  <small>Add your first account to get started with bookkeeping.</small>
                </p>
              </div>
            )}
          </section>
        )}

        {activeView === 'trial-balance' && (
          <section className="dashboard-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
              <h3>📊 Trial Balance</h3>
              <button 
                className="btn-secondary" 
                style={{fontSize: '0.875rem'}}
                onClick={() => {
                  setTrialBalance(null);
                  loadTrialBalance();
                }}
              >
                🔄 Refresh
              </button>
            </div>
            
            {trialBalanceLoading ? (
              <div style={{background: 'white', padding: '3rem', borderRadius: '8px', border: '1px solid #e5e7eb', textAlign: 'center'}}>
                <div className="spinner" style={{margin: '0 auto 1rem'}}></div>
                <p style={{color: '#6b7280'}}>Loading trial balance...</p>
              </div>
            ) : trialBalance ? (
              <div style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden'}}>
                <div style={{padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h4 style={{margin: 0}}>As of {formatIsoDate(trialBalance.as_of_date)}</h4>
                    <span style={{
                      background: trialBalance.totals.is_balanced ? '#dcfce7' : '#fee2e2',
                      color: trialBalance.totals.is_balanced ? '#166534' : '#dc2626',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {trialBalance.totals.is_balanced ? '✓ Balanced' : '⚠ Out of Balance'}
                    </span>
                  </div>
                </div>
                
                {trialBalance.trial_balance.length > 0 ? (
                  <>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '100px 1fr auto auto',
                      gap: '1rem',
                      padding: '0.75rem 1rem',
                      background: '#f9fafb',
                      borderBottom: '1px solid #e5e7eb',
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}>
                      <div>Account #</div>
                      <div>Account Name</div>
                      <div style={{textAlign: 'right'}}>Debit</div>
                      <div style={{textAlign: 'right'}}>Credit</div>
                    </div>
                    
                    {trialBalance.trial_balance.map((account: any, index: number) => (
                      <div key={index} style={{
                        display: 'grid',
                        gridTemplateColumns: '100px 1fr auto auto',
                        gap: '1rem',
                        padding: '0.75rem 1rem',
                        borderBottom: '1px solid #f3f4f6',
                        alignItems: 'center'
                      }}>
                        <div style={{fontFamily: 'monospace', color: '#6b7280', fontSize: '0.875rem'}}>
                          {account.account_number}
                        </div>
                        <div>
                          <div style={{fontWeight: '500'}}>
                            {account.account_name}
                          </div>
                          <div style={{fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize'}}>
                            {account.account_type}
                          </div>
                        </div>
                        <div style={{textAlign: 'right', fontFamily: 'monospace'}}>
                          {account.debit_balance > 0 ? formatCurrency(account.debit_balance) : '-'}
                        </div>
                        <div style={{textAlign: 'right', fontFamily: 'monospace'}}>
                          {account.credit_balance > 0 ? formatCurrency(account.credit_balance) : '-'}
                        </div>
                      </div>
                    ))}
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '100px 1fr auto auto',
                      gap: '1rem',
                      padding: '1rem',
                      background: '#f9fafb',
                      borderTop: '2px solid #e5e7eb',
                      fontWeight: '700'
                    }}>
                      <div></div>
                      <div>TOTALS</div>
                      <div style={{textAlign: 'right', fontFamily: 'monospace'}}>
                        {formatCurrency(trialBalance.totals.total_debits)}
                      </div>
                      <div style={{textAlign: 'right', fontFamily: 'monospace'}}>
                        {formatCurrency(trialBalance.totals.total_credits)}
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{padding: '3rem', textAlign: 'center', color: '#6b7280'}}>
                    <p>No accounts with balances found</p>
                    <p style={{fontSize: '0.875rem'}}>Create some accounts and journal entries to see the trial balance.</p>
                    <button 
                      className="btn-primary" 
                      style={{marginTop: '1rem'}}
                      onClick={() => setActiveView('accounts')}
                    >
                      Manage Accounts
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                <p style={{color: '#6b7280', textAlign: 'center', padding: '3rem'}}>
                  ⚖️ Failed to load trial balance<br />
                  <small>Please try refreshing or contact support if the issue persists</small>
                </p>
              </div>
            )}
          </section>
        )}

        {activeView === 'balance-sheet' && (
          <section className="dashboard-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
              <h3>📄 Balance Sheet</h3>
              <button 
                className="btn-secondary" 
                style={{fontSize: '0.875rem'}}
                onClick={() => {
                  setBalanceSheet(null);
                  loadBalanceSheet();
                }}
              >
                🔄 Refresh
              </button>
            </div>
            
            {balanceSheetLoading ? (
              <div style={{background: 'white', padding: '3rem', borderRadius: '8px', border: '1px solid #e5e7eb', textAlign: 'center'}}>
                <div className="spinner" style={{margin: '0 auto 1rem'}}></div>
                <p style={{color: '#6b7280'}}>Loading balance sheet...</p>
              </div>
            ) : balanceSheet ? (
              <div style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden'}}>
                <div style={{padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h4 style={{margin: 0}}>As of {formatIsoDate(balanceSheet.as_of_date)}</h4>
                    <span style={{
                      background: balanceSheet.balance_sheet.totals.balance_check ? '#dcfce7' : '#fee2e2',
                      color: balanceSheet.balance_sheet.totals.balance_check ? '#166534' : '#dc2626',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {balanceSheet.balance_sheet.totals.balance_check ? '✓ Balanced' : '⚠ Out of Balance'}
                    </span>
                  </div>
                </div>
                
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', padding: '1.5rem'}}>
                  {/* Assets */}
                  <div>
                    <h4 style={{
                      background: '#f0f9ff',
                      margin: '0 -1rem 1rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      color: '#0369a1',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>
                      💰 ASSETS
                    </h4>
                    
                    {balanceSheet.balance_sheet.assets.length > 0 ? (
                      <>
                        {balanceSheet.balance_sheet.assets.map((account: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem 0',
                            borderBottom: '1px solid #f3f4f6'
                          }}>
                            <div>
                              <div style={{fontWeight: '500'}}>
                                {account.account_name}
                              </div>
                              {account.sub_type && (
                                <div style={{fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize'}}>
                                  {account.sub_type.replace(/_/g, ' ')}
                                </div>
                              )}
                            </div>
                            <div style={{fontFamily: 'monospace', fontWeight: '500'}}>
                              {formatCurrency(account.balance)}
                            </div>
                          </div>
                        ))}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem 0 0.5rem',
                          borderTop: '2px solid #e5e7eb',
                          fontWeight: '700',
                          fontSize: '1.1rem'
                        }}>
                          <div>TOTAL ASSETS</div>
                          <div style={{fontFamily: 'monospace'}}>
                            {formatCurrency(balanceSheet.balance_sheet.totals.total_assets)}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p style={{color: '#6b7280', fontStyle: 'italic'}}>No asset accounts</p>
                    )}
                  </div>

                  {/* Liabilities & Equity */}
                  <div>
                    <h4 style={{
                      background: '#fef3c7',
                      margin: '0 -1rem 1rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      color: '#d97706',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>
                      📋 LIABILITIES
                    </h4>
                    
                    {balanceSheet.balance_sheet.liabilities.length > 0 ? (
                      <>
                        {balanceSheet.balance_sheet.liabilities.map((account: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem 0',
                            borderBottom: '1px solid #f3f4f6'
                          }}>
                            <div>
                              <div style={{fontWeight: '500'}}>
                                {account.account_name}
                              </div>
                              {account.sub_type && (
                                <div style={{fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize'}}>
                                  {account.sub_type.replace(/_/g, ' ')}
                                </div>
                              )}
                            </div>
                            <div style={{fontFamily: 'monospace', fontWeight: '500'}}>
                              {formatCurrency(account.balance)}
                            </div>
                          </div>
                        ))}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem 0 0.5rem',
                          borderBottom: '1px solid #e5e7eb',
                          fontWeight: '700'
                        }}>
                          <div>TOTAL LIABILITIES</div>
                          <div style={{fontFamily: 'monospace'}}>
                            {formatCurrency(balanceSheet.balance_sheet.totals.total_liabilities)}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p style={{color: '#6b7280', fontStyle: 'italic', marginBottom: '2rem'}}>No liability accounts</p>
                    )}

                    <h4 style={{
                      background: '#f0fdf4',
                      margin: '1.5rem -1rem 1rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      color: '#15803d',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>
                      🏦 EQUITY
                    </h4>
                    
                    {balanceSheet.balance_sheet.equity.length > 0 ? (
                      <>
                        {balanceSheet.balance_sheet.equity.map((account: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem 0',
                            borderBottom: '1px solid #f3f4f6'
                          }}>
                            <div>
                              <div style={{fontWeight: '500'}}>
                                {account.account_name}
                              </div>
                              {account.sub_type && (
                                <div style={{fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize'}}>
                                  {account.sub_type.replace(/_/g, ' ')}
                                </div>
                              )}
                            </div>
                            <div style={{fontFamily: 'monospace', fontWeight: '500'}}>
                              {formatCurrency(account.balance)}
                            </div>
                          </div>
                        ))}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem 0 0.5rem',
                          borderBottom: '1px solid #e5e7eb',
                          fontWeight: '700'
                        }}>
                          <div>TOTAL EQUITY</div>
                          <div style={{fontFamily: 'monospace'}}>
                            {formatCurrency(balanceSheet.balance_sheet.totals.total_equity)}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p style={{color: '#6b7280', fontStyle: 'italic'}}>No equity accounts</p>
                    )}

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem 0',
                      borderTop: '2px solid #e5e7eb',
                      fontWeight: '700',
                      fontSize: '1.1rem',
                      background: '#f9fafb',
                      margin: '1rem -1rem 0',
                      paddingLeft: '1rem',
                      paddingRight: '1rem'
                    }}>
                      <div>TOTAL LIABILITIES + EQUITY</div>
                      <div style={{fontFamily: 'monospace'}}>
                        {formatCurrency(balanceSheet.balance_sheet.totals.total_liabilities + balanceSheet.balance_sheet.totals.total_equity)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Balance Check Summary */}
                <div style={{
                  padding: '1rem 1.5rem',
                  background: balanceSheet.balance_sheet.totals.balance_check ? '#f0fdf4' : '#fef2f2',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{
                    color: balanceSheet.balance_sheet.totals.balance_check ? '#15803d' : '#dc2626',
                    fontWeight: '600'
                  }}>
                    {balanceSheet.balance_sheet.totals.balance_check ? 
                      '✓ Assets = Liabilities + Equity (Balanced)' : 
                      '⚠ Assets ≠ Liabilities + Equity (Out of Balance)'
                    }
                  </div>
                  <div style={{
                    fontFamily: 'monospace',
                    color: balanceSheet.balance_sheet.totals.balance_check ? '#15803d' : '#dc2626',
                    fontWeight: '700'
                  }}>
                    Difference: {formatCurrency(
                      balanceSheet.balance_sheet.totals.total_assets - 
                      (balanceSheet.balance_sheet.totals.total_liabilities + balanceSheet.balance_sheet.totals.total_equity)
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                <p style={{color: '#6b7280', textAlign: 'center', padding: '3rem'}}>
                  🏦 Failed to load balance sheet<br />
                  <small>Please try refreshing or contact support if the issue persists</small>
                </p>
              </div>
            )}
          </section>
        )}

        {activeView === 'income-statement' && (
          <section className="dashboard-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
              <h3>📈 Income Statement (P&L)</h3>
              <button 
                className="btn-secondary" 
                style={{fontSize: '0.875rem'}}
                onClick={() => {
                  setIncomeStatement(null);
                  loadIncomeStatement();
                }}
              >
                🔄 Refresh
              </button>
            </div>
            
            {incomeStatementLoading ? (
              <div style={{background: 'white', padding: '3rem', borderRadius: '8px', border: '1px solid #e5e7eb', textAlign: 'center'}}>
                <div className="spinner" style={{margin: '0 auto 1rem'}}></div>
                <p style={{color: '#6b7280'}}>Loading income statement...</p>
              </div>
            ) : incomeStatement ? (
              <div style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden'}}>
                <div style={{padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h4 style={{margin: 0}}>
                      For the period: {formatIsoDate(incomeStatement.start_date)} - {formatIsoDate(incomeStatement.end_date)}
                    </h4>
                    <span style={{
                      background: incomeStatement.income_statement.totals.net_income >= 0 ? '#dcfce7' : '#fee2e2',
                      color: incomeStatement.income_statement.totals.net_income >= 0 ? '#166534' : '#dc2626',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {incomeStatement.income_statement.totals.net_income >= 0 ? '💰 Profit' : '📉 Loss'}
                    </span>
                  </div>
                </div>
                
                <div style={{padding: '1.5rem'}}>
                  {/* Revenue Section */}
                  <div style={{marginBottom: '2rem'}}>
                    <h4 style={{
                      background: '#f0fdf4',
                      margin: '0 -1.5rem 1rem',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      color: '#15803d',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>
                      📈 REVENUE
                    </h4>
                    
                    {incomeStatement.income_statement.revenue.length > 0 ? (
                      <>
                        {incomeStatement.income_statement.revenue.map((account: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem 0',
                            borderBottom: '1px solid #f3f4f6'
                          }}>
                            <div>
                              <div style={{fontWeight: '500'}}>
                                {account.account_name}
                              </div>
                              {account.sub_type && (
                                <div style={{fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize'}}>
                                  {account.sub_type.replace(/_/g, ' ')}
                                </div>
                              )}
                            </div>
                            <div style={{fontFamily: 'monospace', fontWeight: '500', color: '#15803d'}}>
                              {formatCurrency(account.amount)}
                            </div>
                          </div>
                        ))}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem 0 0.5rem',
                          borderTop: '2px solid #e5e7eb',
                          fontWeight: '700',
                          fontSize: '1.1rem'
                        }}>
                          <div>TOTAL REVENUE</div>
                          <div style={{fontFamily: 'monospace', color: '#15803d'}}>
                            {formatCurrency(incomeStatement.income_statement.totals.total_revenue)}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p style={{color: '#6b7280', fontStyle: 'italic'}}>No revenue accounts with activity</p>
                    )}
                  </div>

                  {/* Expenses Section */}
                  <div style={{marginBottom: '2rem'}}>
                    <h4 style={{
                      background: '#fef3c7',
                      margin: '0 -1.5rem 1rem',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      color: '#d97706',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>
                      💸 EXPENSES
                    </h4>
                    
                    {incomeStatement.income_statement.expenses.length > 0 ? (
                      <>
                        {incomeStatement.income_statement.expenses.map((account: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem 0',
                            borderBottom: '1px solid #f3f4f6'
                          }}>
                            <div>
                              <div style={{fontWeight: '500'}}>
                                {account.account_name}
                              </div>
                              {account.sub_type && (
                                <div style={{fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize'}}>
                                  {account.sub_type.replace(/_/g, ' ')}
                                </div>
                              )}
                            </div>
                            <div style={{fontFamily: 'monospace', fontWeight: '500', color: '#d97706'}}>
                              {formatCurrency(account.amount)}
                            </div>
                          </div>
                        ))}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem 0 0.5rem',
                          borderTop: '2px solid #e5e7eb',
                          fontWeight: '700',
                          fontSize: '1.1rem'
                        }}>
                          <div>TOTAL EXPENSES</div>
                          <div style={{fontFamily: 'monospace', color: '#d97706'}}>
                            {formatCurrency(incomeStatement.income_statement.totals.total_expenses)}
                          </div>
                        </div>
                        {!!incomeStatement.income_statement.totals.cost_of_sales && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.75rem 0 0',
                            fontWeight: '600'
                          }}>
                            <div>
                              GROSS PROFIT
                              <div style={{fontSize: '0.8rem', fontWeight: '400', color: '#6b7280'}}>
                                Revenue less cost of sales
                                {incomeStatement.income_statement.totals.total_revenue > 0 && (
                                  <> ({Math.round((incomeStatement.income_statement.totals.gross_profit || 0)
                                      / incomeStatement.income_statement.totals.total_revenue * 100)}% margin)</>
                                )}
                              </div>
                            </div>
                            <div style={{fontFamily: 'monospace', color: '#059669'}}>
                              {formatCurrency(incomeStatement.income_statement.totals.gross_profit || 0)}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p style={{color: '#6b7280', fontStyle: 'italic'}}>No expense accounts with activity</p>
                    )}
                  </div>

                  {/* Net Income Summary */}
                  <div style={{
                    padding: '1.5rem',
                    background: incomeStatement.income_statement.totals.net_income >= 0 ? '#f0fdf4' : '#fef2f2',
                    borderRadius: '8px',
                    border: `2px solid ${incomeStatement.income_statement.totals.net_income >= 0 ? '#22c55e' : '#ef4444'}`,
                    marginTop: '1rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '1.25rem',
                      fontWeight: '700'
                    }}>
                      <div style={{
                        color: incomeStatement.income_statement.totals.net_income >= 0 ? '#15803d' : '#dc2626'
                      }}>
                        {incomeStatement.income_statement.totals.net_income >= 0 ? '💰 NET INCOME (PROFIT)' : '📉 NET LOSS'}
                      </div>
                      <div style={{
                        fontFamily: 'monospace',
                        color: incomeStatement.income_statement.totals.net_income >= 0 ? '#15803d' : '#dc2626'
                      }}>
                        {formatCurrency(incomeStatement.income_statement.totals.net_income)}
                      </div>
                    </div>
                    
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: 'white',
                      borderRadius: '6px',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '1rem',
                      fontSize: '0.875rem'
                    }}>
                      <div style={{textAlign: 'center'}}>
                        <div style={{color: '#6b7280', marginBottom: '0.25rem'}}>Revenue</div>
                        <div style={{fontWeight: '600', color: '#15803d'}}>
                          {formatCurrency(incomeStatement.income_statement.totals.total_revenue)}
                        </div>
                      </div>
                      <div style={{textAlign: 'center'}}>
                        <div style={{color: '#6b7280', marginBottom: '0.25rem'}}>Expenses</div>
                        <div style={{fontWeight: '600', color: '#d97706'}}>
                          {formatCurrency(incomeStatement.income_statement.totals.total_expenses)}
                        </div>
                      </div>
                      <div style={{textAlign: 'center'}}>
                        <div style={{color: '#6b7280', marginBottom: '0.25rem'}}>Net Income</div>
                        <div style={{
                          fontWeight: '700',
                          color: incomeStatement.income_statement.totals.net_income >= 0 ? '#15803d' : '#dc2626'
                        }}>
                          {formatCurrency(incomeStatement.income_statement.totals.net_income)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                <p style={{color: '#6b7280', textAlign: 'center', padding: '3rem'}}>
                  📈 Failed to load income statement<br />
                  <small>Please try refreshing or contact support if the issue persists</small>
                </p>
              </div>
            )}
          </section>
        )}

        {activeView === 'cash-flow' && (
          <section className="dashboard-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
              <h3>💵 Cash Flow Statement</h3>
              <button
                className="btn-secondary"
                style={{fontSize: '0.875rem'}}
                onClick={() => {
                  setCashFlow(null);
                  loadCashFlow();
                }}
              >
                🔄 Refresh
              </button>
            </div>

            {cashFlowLoading ? (
              <div style={{background: 'white', padding: '3rem', borderRadius: '8px', border: '1px solid #e5e7eb', textAlign: 'center'}}>
                <div className="spinner" style={{margin: '0 auto 1rem'}}></div>
                <p style={{color: '#6b7280'}}>Loading cash flow statement...</p>
              </div>
            ) : cashFlow ? (
              <div style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden'}}>
                <div style={{padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h4 style={{margin: 0}}>
                      For the period: {formatIsoDate(cashFlow.start_date)} - {formatIsoDate(cashFlow.end_date)}
                    </h4>
                    <span style={{
                      background: cashFlow.cash_flow_statement.summary.reconciliation_check ? '#dcfce7' : '#fee2e2',
                      color: cashFlow.cash_flow_statement.summary.reconciliation_check ? '#166534' : '#dc2626',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      {cashFlow.cash_flow_statement.summary.reconciliation_check ? '✓ Reconciled' : '⚠ Not Reconciled'}
                    </span>
                  </div>
                </div>

                <div style={{padding: '1.5rem'}}>
                  {/* Operating Activities */}
                  <div style={{marginBottom: '2rem'}}>
                    <h4 style={{
                      background: '#f0fdf4',
                      margin: '0 -1.5rem 1rem',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      color: '#15803d',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>
                      OPERATING ACTIVITIES
                    </h4>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid #f3f4f6',
                      fontWeight: '500'
                    }}>
                      <div>Net Income</div>
                      <div style={{fontFamily: 'monospace', fontWeight: '500', color: cashFlow.cash_flow_statement.operating_activities.net_income >= 0 ? '#15803d' : '#dc2626'}}>
                        {formatCurrency(cashFlow.cash_flow_statement.operating_activities.net_income)}
                      </div>
                    </div>

                    {cashFlow.cash_flow_statement.operating_activities.adjustments.length > 0 && (
                      <>
                        <div style={{fontSize: '0.875rem', color: '#6b7280', padding: '0.75rem 0 0.25rem', fontStyle: 'italic'}}>
                          Adjustments for changes in working capital:
                        </div>
                        {cashFlow.cash_flow_statement.operating_activities.adjustments.map((adj: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.35rem 0 0.35rem 1rem',
                            borderBottom: '1px solid #f3f4f6',
                            fontSize: '0.9rem'
                          }}>
                            <div>{adj.account_name}</div>
                            <div style={{fontFamily: 'monospace', color: adj.cash_impact >= 0 ? '#15803d' : '#dc2626'}}>
                              {formatCurrency(adj.cash_impact)}
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem 0 0.5rem',
                      borderTop: '2px solid #e5e7eb',
                      fontWeight: '700',
                      fontSize: '1rem'
                    }}>
                      <div>Net Cash from Operating</div>
                      <div style={{fontFamily: 'monospace', color: cashFlow.cash_flow_statement.operating_activities.net_cash_from_operating >= 0 ? '#15803d' : '#dc2626'}}>
                        {formatCurrency(cashFlow.cash_flow_statement.operating_activities.net_cash_from_operating)}
                      </div>
                    </div>
                  </div>

                  {/* Investing Activities */}
                  <div style={{marginBottom: '2rem'}}>
                    <h4 style={{
                      background: '#f0f9ff',
                      margin: '0 -1.5rem 1rem',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      color: '#0369a1',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>
                      INVESTING ACTIVITIES
                    </h4>

                    {cashFlow.cash_flow_statement.investing_activities.items.length > 0 ? (
                      <>
                        {cashFlow.cash_flow_statement.investing_activities.items.map((item: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem 0',
                            borderBottom: '1px solid #f3f4f6'
                          }}>
                            <div style={{fontWeight: '500'}}>{item.account_name}</div>
                            <div style={{fontFamily: 'monospace', fontWeight: '500', color: item.cash_impact >= 0 ? '#15803d' : '#dc2626'}}>
                              {formatCurrency(item.cash_impact)}
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <p style={{color: '#6b7280', fontStyle: 'italic'}}>No investing activities</p>
                    )}

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem 0 0.5rem',
                      borderTop: '2px solid #e5e7eb',
                      fontWeight: '700',
                      fontSize: '1rem'
                    }}>
                      <div>Net Cash from Investing</div>
                      <div style={{fontFamily: 'monospace', color: cashFlow.cash_flow_statement.investing_activities.net_cash_from_investing >= 0 ? '#15803d' : '#dc2626'}}>
                        {formatCurrency(cashFlow.cash_flow_statement.investing_activities.net_cash_from_investing)}
                      </div>
                    </div>
                  </div>

                  {/* Financing Activities */}
                  <div style={{marginBottom: '2rem'}}>
                    <h4 style={{
                      background: '#fef3c7',
                      margin: '0 -1.5rem 1rem',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      color: '#d97706',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>
                      FINANCING ACTIVITIES
                    </h4>

                    {cashFlow.cash_flow_statement.financing_activities.items.length > 0 ? (
                      <>
                        {cashFlow.cash_flow_statement.financing_activities.items.map((item: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem 0',
                            borderBottom: '1px solid #f3f4f6'
                          }}>
                            <div style={{fontWeight: '500'}}>{item.account_name}</div>
                            <div style={{fontFamily: 'monospace', fontWeight: '500', color: item.cash_impact >= 0 ? '#15803d' : '#dc2626'}}>
                              {formatCurrency(item.cash_impact)}
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <p style={{color: '#6b7280', fontStyle: 'italic'}}>No financing activities</p>
                    )}

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '1rem 0 0.5rem',
                      borderTop: '2px solid #e5e7eb',
                      fontWeight: '700',
                      fontSize: '1rem'
                    }}>
                      <div>Net Cash from Financing</div>
                      <div style={{fontFamily: 'monospace', color: cashFlow.cash_flow_statement.financing_activities.net_cash_from_financing >= 0 ? '#15803d' : '#dc2626'}}>
                        {formatCurrency(cashFlow.cash_flow_statement.financing_activities.net_cash_from_financing)}
                      </div>
                    </div>
                  </div>

                  {/* Cash Summary */}
                  <div style={{
                    padding: '1.5rem',
                    background: cashFlow.cash_flow_statement.summary.net_change_in_cash >= 0 ? '#f0fdf4' : '#fef2f2',
                    borderRadius: '8px',
                    border: `2px solid ${cashFlow.cash_flow_statement.summary.net_change_in_cash >= 0 ? '#22c55e' : '#ef4444'}`,
                    marginTop: '1rem'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '1rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase'}}>Opening Cash</div>
                        <div style={{fontFamily: 'monospace', fontWeight: '600', fontSize: '1.1rem'}}>
                          {formatCurrency(cashFlow.cash_flow_statement.summary.opening_cash_balance)}
                        </div>
                      </div>
                      <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase'}}>Net Change</div>
                        <div style={{
                          fontFamily: 'monospace',
                          fontWeight: '600',
                          fontSize: '1.1rem',
                          color: cashFlow.cash_flow_statement.summary.net_change_in_cash >= 0 ? '#15803d' : '#dc2626'
                        }}>
                          {cashFlow.cash_flow_statement.summary.net_change_in_cash >= 0 ? '+' : ''}{formatCurrency(cashFlow.cash_flow_statement.summary.net_change_in_cash)}
                        </div>
                      </div>
                      <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase'}}>Closing Cash</div>
                        <div style={{fontFamily: 'monospace', fontWeight: '600', fontSize: '1.1rem'}}>
                          {formatCurrency(cashFlow.cash_flow_statement.summary.closing_cash_balance)}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '1.25rem',
                      fontWeight: '700',
                      paddingTop: '1rem',
                      borderTop: '2px solid rgba(0,0,0,0.1)'
                    }}>
                      <div style={{
                        color: cashFlow.cash_flow_statement.summary.net_change_in_cash >= 0 ? '#15803d' : '#dc2626'
                      }}>
                        {cashFlow.cash_flow_statement.summary.net_change_in_cash >= 0 ? '💰 NET CASH INCREASE' : '📉 NET CASH DECREASE'}
                      </div>
                      <div style={{
                        fontFamily: 'monospace',
                        color: cashFlow.cash_flow_statement.summary.net_change_in_cash >= 0 ? '#15803d' : '#dc2626'
                      }}>
                        {formatCurrency(Math.abs(cashFlow.cash_flow_statement.summary.net_change_in_cash))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{background: 'white', padding: '3rem', borderRadius: '8px', border: '1px solid #e5e7eb', textAlign: 'center'}}>
                <p style={{color: '#6b7280'}}>
                  💵 Failed to load cash flow statement<br />
                  <small>Please try refreshing or contact support if the issue persists</small>
                </p>
              </div>
            )}
          </section>
        )}

        {activeView === 'general-ledger' && (
          <section className="dashboard-section">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem'}}>
              <h3>📒 General Ledger</h3>
              <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                <select
                  value={glAccountFilter || ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value) : undefined;
                    setGlAccountFilter(val);
                    setGeneralLedger(null);
                    loadGeneralLedger(val);
                  }}
                  style={{padding: '0.4rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.875rem'}}
                >
                  <option value="">All Accounts</option>
                  {accounts && Object.entries(accounts).map(([type, accts]) =>
                    (accts as any[]).map((acc: any) => (
                      <option key={acc.id} value={acc.id}>
                        {acc.account_number} - {acc.account_name}
                      </option>
                    ))
                  )}
                </select>
                <button
                  className="btn-secondary"
                  style={{fontSize: '0.875rem'}}
                  onClick={() => {
                    setGeneralLedger(null);
                    loadGeneralLedger(glAccountFilter);
                  }}
                >
                  🔄 Refresh
                </button>
              </div>
            </div>

            {generalLedgerLoading ? (
              <div style={{background: 'white', padding: '3rem', borderRadius: '8px', border: '1px solid #e5e7eb', textAlign: 'center'}}>
                <div className="spinner" style={{margin: '0 auto 1rem'}}></div>
                <p style={{color: '#6b7280'}}>Loading general ledger...</p>
              </div>
            ) : generalLedger && generalLedger.general_ledger?.length > 0 ? (
              <div>
                {generalLedger.general_ledger.map((account: any) => (
                  <div key={account.account_id} style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '1rem', overflow: 'hidden'}}>
                    <div style={{padding: '0.75rem 1rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <strong>{account.account_number} - {account.account_name}</strong>
                      <span style={{fontSize: '0.8rem', color: '#6b7280', textTransform: 'capitalize'}}>{account.account_type}</span>
                    </div>
                    <div style={{overflowX: 'auto'}}>
                      <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem'}}>
                        <thead>
                          <tr style={{background: '#f3f4f6'}}>
                            <th style={{padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Date</th>
                            <th style={{padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Entry #</th>
                            <th style={{padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Description</th>
                            <th style={{padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Reference</th>
                            <th style={{padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb'}}>Debit</th>
                            <th style={{padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb'}}>Credit</th>
                            <th style={{padding: '0.5rem', textAlign: 'right', borderBottom: '1px solid #e5e7eb'}}>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{background: '#fefce8'}}>
                            <td colSpan={6} style={{padding: '0.4rem 0.5rem', fontStyle: 'italic', color: '#6b7280'}}>Opening Balance</td>
                            <td style={{padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: '600'}}>{formatCurrency(account.opening_balance)}</td>
                          </tr>
                          {account.entries.map((entry: any, idx: number) => (
                            <tr key={idx} style={{borderBottom: '1px solid #f3f4f6'}}>
                              <td style={{padding: '0.4rem 0.5rem'}}>{entry.entry_date ? formatIsoDate(entry.entry_date) : ''}</td>
                              <td style={{padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontSize: '0.8rem'}}>{entry.entry_number}</td>
                              <td style={{padding: '0.4rem 0.5rem'}}>{entry.description}</td>
                              <td style={{padding: '0.4rem 0.5rem', color: '#6b7280'}}>{entry.reference}</td>
                              <td style={{padding: '0.4rem 0.5rem', textAlign: 'right', color: entry.debit > 0 ? '#dc2626' : undefined}}>{entry.debit > 0 ? formatCurrency(entry.debit) : ''}</td>
                              <td style={{padding: '0.4rem 0.5rem', textAlign: 'right', color: entry.credit > 0 ? '#059669' : undefined}}>{entry.credit > 0 ? formatCurrency(entry.credit) : ''}</td>
                              <td style={{padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: '500'}}>{formatCurrency(entry.running_balance)}</td>
                            </tr>
                          ))}
                          <tr style={{background: '#f0fdf4', fontWeight: '700'}}>
                            <td colSpan={6} style={{padding: '0.5rem', borderTop: '2px solid #059669'}}>Closing Balance</td>
                            <td style={{padding: '0.5rem', textAlign: 'right', borderTop: '2px solid #059669'}}>{formatCurrency(account.closing_balance)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ) : generalLedger ? (
              <div style={{background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                <p style={{color: '#6b7280', textAlign: 'center', padding: '3rem'}}>
                  📒 No entries found for the selected period
                </p>
              </div>
            ) : (
              <div style={{background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                <p style={{color: '#6b7280', textAlign: 'center', padding: '3rem'}}>
                  📒 Failed to load general ledger<br />
                  <small>Please try refreshing or contact support if the issue persists</small>
                </p>
              </div>
            )}
          </section>
        )}

        {activeView === 'journal-entries' && (
          <section className="dashboard-section">
            <h3 style={{marginBottom: '1rem'}}>📋 Journal Entries</h3>

            {/* Search Filters */}
            <div style={{background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '1rem'}}>
              <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end'}}>
                <div style={{flex: '1', minWidth: '200px'}}>
                  <label style={{fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem'}}>Search</label>
                  <input type="text" placeholder="Description, reference, entry #..." value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadSearchEntries()}
                    style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px'}} />
                </div>
                <div>
                  <label style={{fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem'}}>Status</label>
                  <select value={searchStatus} onChange={e => setSearchStatus(e.target.value)}
                    style={{padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px'}}>
                    <option value="">All</option>
                    <option value="draft">Draft</option>
                    <option value="posted">Posted</option>
                    <option value="void">Void</option>
                  </select>
                </div>
                <div>
                  <label style={{fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem'}}>From</label>
                  <input type="date" value={searchStartDate} onChange={e => setSearchStartDate(e.target.value)}
                    style={{padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px'}} />
                </div>
                <div>
                  <label style={{fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem'}}>To</label>
                  <input type="date" value={searchEndDate} onChange={e => setSearchEndDate(e.target.value)}
                    style={{padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px'}} />
                </div>
                <button className="btn-primary" onClick={() => loadSearchEntries()} style={{padding: '0.5rem 1rem'}}>
                  Search
                </button>
              </div>
            </div>

            {/* Results */}
            {searchLoading ? (
              <p>Loading entries...</p>
            ) : (
              <div style={{background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem'}}>
                  <thead>
                    <tr style={{background: '#f9fafb', borderBottom: '2px solid #e5e7eb'}}>
                      <th style={{padding: '0.75rem', textAlign: 'left'}}>Entry #</th>
                      <th style={{padding: '0.75rem', textAlign: 'left'}}>Date</th>
                      <th style={{padding: '0.75rem', textAlign: 'left'}}>Type</th>
                      <th style={{padding: '0.75rem', textAlign: 'left'}}>Description</th>
                      <th style={{padding: '0.75rem', textAlign: 'right'}}>Amount</th>
                      <th style={{padding: '0.75rem', textAlign: 'center'}}>Status</th>
                      <th style={{padding: '0.75rem', textAlign: 'center'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchEntries.length === 0 ? (
                      <tr><td colSpan={7} style={{padding: '2rem', textAlign: 'center', color: '#9ca3af'}}>No entries found</td></tr>
                    ) : searchEntries.map((entry: any) => (
                      <React.Fragment key={entry.id}>
                      <tr
                        style={{borderBottom: '1px solid #f3f4f6', cursor: 'pointer'}}
                        onClick={() => setExpandedEntryId(expandedEntryId === entry.id ? null : entry.id)}
                        title="Show the debits and credits"
                      >
                        <td style={{padding: '0.75rem', fontFamily: 'monospace'}}>
                          <span style={{color: '#9ca3af', marginRight: '0.4rem'}}>{expandedEntryId === entry.id ? '▾' : '▸'}</span>
                          {entry.entry_number}
                        </td>
                        <td style={{padding: '0.75rem'}}>{entry.entry_date}</td>
                        <td style={{padding: '0.75rem', textTransform: 'capitalize'}}>{(entry.entry_type || '').replace(/_/g, ' ')}</td>
                        <td style={{padding: '0.75rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          {entry.description || '—'}
                        </td>
                        <td style={{padding: '0.75rem', textAlign: 'right', fontFamily: 'monospace'}}>
                          {business.currency === 'CAD' ? 'C$' : '$'}{Number(entry.total_amount).toFixed(2)}
                        </td>
                        <td style={{padding: '0.75rem', textAlign: 'center'}}>
                          <span style={{
                            padding: '0.25rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600,
                            background: entry.status === 'posted' ? '#d1fae5' : entry.status === 'void' ? '#fee2e2' : '#fef3c7',
                            color: entry.status === 'posted' ? '#059669' : entry.status === 'void' ? '#dc2626' : '#d97706'
                          }}>
                            {entry.status}
                          </span>
                        </td>
                        <td style={{padding: '0.75rem', textAlign: 'center'}} onClick={(e) => e.stopPropagation()}>
                          <div style={{display: 'flex', gap: '0.25rem', justifyContent: 'center'}}>
                            <button onClick={() => toggleAttachments(entry.id)}
                              style={{
                                padding: '0.25rem 0.5rem', fontSize: '0.85rem',
                                background: attachmentsEntryId === entry.id ? '#dbeafe' : '#f3f4f6',
                                color: attachmentsEntryId === entry.id ? '#2563eb' : '#6b7280',
                                border: 'none', borderRadius: '4px', cursor: 'pointer'
                              }}
                              title="Invoices, receipts and other support">
                              {String.fromCodePoint(0x1F4CE)}
                            </button>
                            {entry.status === 'draft' && (
                              <button onClick={() => handlePostJournalEntry(entry.id)}
                                style={{...entryActionButton, background: '#d1fae5', color: '#059669'}}
                                title="Post this draft to the books">Post</button>
                            )}
                            {entry.status === 'draft' && (
                              <button onClick={() => handleEditEntry(entry)}
                                style={{...entryActionButton, background: '#eff6ff', color: '#2563eb'}}
                                title="Edit this draft">Edit</button>
                            )}
                            <button onClick={() => handleCopyEntry(entry)}
                              style={{...entryActionButton, background: '#f3f4f6', color: '#374151'}}
                              title="Start a new entry with these lines">Copy</button>
                            {entry.status === 'posted' ? (
                              <button onClick={() => handleVoidEntry(entry.id)} style={entryActionButton} title="Void entry">Void</button>
                            ) : (
                              <button onClick={() => handleDeleteEntry(entry.id)} style={entryActionButton}
                                title={entry.status === 'draft' ? 'Delete draft' : 'Delete voided entry'}>Delete</button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Attachments panel - expands below entry row */}
                      {expandedEntryId === entry.id && (
                        <tr>
                          <td colSpan={7} style={{padding: '0 1rem 0.75rem', background: '#f8fafc'}}>
                            {/* Fixed columns so every entry's debits and credits sit in the same two columns */}
                            <table style={{width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '0.85rem'}}>
                              <colgroup>
                                <col style={{width: '34%'}} />
                                <col style={{width: '36%'}} />
                                <col style={{width: '15%'}} />
                                <col style={{width: '15%'}} />
                              </colgroup>
                              <thead>
                                <tr style={{color: '#6b7280'}}>
                                  <th style={lineCell}>Account</th>
                                  <th style={lineCell}>Line description</th>
                                  <th style={amountCell}>Debit</th>
                                  <th style={amountCell}>Credit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(entry.lines || []).map((line: any) => (
                                  <tr key={line.id}>
                                    <td style={lineCell}>{line.account_name || line.account_id}</td>
                                    <td style={{...lineCell, color: '#6b7280'}}>{line.description || '—'}</td>
                                    <td style={amountCell}>
                                      {Number(line.debit_amount) ? formatCurrency(Number(line.debit_amount)) : ''}
                                    </td>
                                    <td style={amountCell}>
                                      {Number(line.credit_amount) ? formatCurrency(Number(line.credit_amount)) : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                      {attachmentsEntryId === entry.id && (
                        <tr>
                          <td colSpan={7} style={{padding: '0.75rem 1rem', background: '#f8fafc', borderBottom: '2px solid #e5e7eb'}}>
                            <EntryAttachments businessId={business.id} entryId={entry.id} />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {searchTotal > 25 && (
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', borderTop: '1px solid #e5e7eb'}}>
                    <span style={{fontSize: '0.875rem', color: '#6b7280'}}>
                      Showing {(searchPage - 1) * 25 + 1}–{Math.min(searchPage * 25, searchTotal)} of {searchTotal}
                    </span>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                      <button disabled={searchPage <= 1} onClick={() => loadSearchEntries(searchPage - 1)}
                        style={{padding: '0.25rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '4px', cursor: searchPage <= 1 ? 'not-allowed' : 'pointer'}}>
                        Prev
                      </button>
                      <button disabled={searchPage * 25 >= searchTotal} onClick={() => loadSearchEntries(searchPage + 1)}
                        style={{padding: '0.25rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '4px', cursor: searchPage * 25 >= searchTotal ? 'not-allowed' : 'pointer'}}>
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {activeView === 'periods' && (
          <PeriodManager businessId={business.id} />
        )}

        {activeView === 'reconciliation' && (
          <BankReconciliation
            businessId={business.id}
            accounts={accounts ? Object.values(accounts).flat() : []}
            currency={business.currency}
          />
        )}

        {activeView === 'budget' && (
          <section className="dashboard-section">
            <h3>📊 Budget vs Actual</h3>
            <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
              <BudgetManager businessId={business.id} formatCurrency={formatCurrency} />
            </div>
          </section>
        )}

        {activeView === 'contacts' && (
          <ContactsManager businessId={business.id} country={business.country} />
        )}

        {activeView === 'invoices' && (
          <InvoiceManager businessId={business.id} formatCurrency={formatCurrency} />
        )}

        {activeView === 'aging' && (
          <AgingReport businessId={business.id} />
        )}

        {activeView === 'sales-tax' && (
          <SalesTaxReport businessId={business.id} startDate={business.business_start_date} />
        )}

        {activeView === 'settings' && (
          <section className="dashboard-section">
            <h3>⚙️ Business Settings</h3>
            <div style={{background: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
              <form onSubmit={handleBusinessSettingsSubmit} style={{maxWidth: '800px'}}>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem'}}>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Business Name *</label>
                    <input
                      type="text"
                      value={businessSettingsForm.business_name}
                      onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, business_name: e.target.value})}
                      required
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Business Type *</label>
                    <select
                      value={businessSettingsForm.business_type}
                      onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, business_type: e.target.value as any})}
                      required
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    >
                      <option value="sole_proprietorship">Sole Proprietorship</option>
                      <option value="partnership">Partnership</option>
                      <option value="corporation">Corporation</option>
                      <option value="llc">LLC</option>
                    </select>
                  </div>
                </div>

                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem'}}>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Industry</label>
                    <input
                      type="text"
                      value={businessSettingsForm.industry}
                      onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, industry: e.target.value})}
                      placeholder="e.g., Technology, Retail, Consulting"
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>
                      {business.country === 'Canada' ? 'GST/HST Number' : 'Tax ID'} <span style={{color: '#6b7280', fontWeight: 400}}>(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={businessSettingsForm.tax_registration_number}
                      onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, tax_registration_number: e.target.value})}
                      placeholder={business.country === 'Canada' ? 'e.g., 80231 4567 RT0001' : 'e.g., 12-3456789'}
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                    <p style={{color: '#6b7280', fontSize: '0.8rem', margin: '0.25rem 0 0'}}>
                      Printed on your invoices. Customers need it to claim the tax back.
                    </p>
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Phone</label>
                    <input
                      type="tel"
                      value={businessSettingsForm.phone}
                      onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                </div>

                <div style={{marginBottom: '2rem'}}>
                  <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Email</label>
                  <input
                    type="email"
                    value={businessSettingsForm.email}
                    onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, email: e.target.value})}
                    placeholder="business@example.com"
                    style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                  />
                </div>

                <div style={{marginBottom: '2rem'}}>
                  <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Address</label>
                  <input
                    type="text"
                    value={businessSettingsForm.address}
                    onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, address: e.target.value})}
                    placeholder="123 Business Street"
                    style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                  />
                </div>

                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '2rem'}}>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>City</label>
                    <input
                      type="text"
                      value={businessSettingsForm.city}
                      onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, city: e.target.value})}
                      placeholder="Toronto"
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>State/Province</label>
                    <input
                      type="text"
                      value={businessSettingsForm.state_province}
                      onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, state_province: e.target.value})}
                      placeholder="ON"
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Postal Code</label>
                    <input
                      type="text"
                      value={businessSettingsForm.postal_code}
                      onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, postal_code: e.target.value})}
                      placeholder="M5V 3A8"
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                </div>

                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem'}}>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Country *</label>
                    <select
                      value={businessSettingsForm.country}
                      onChange={(e) => {
                        const newCountry = e.target.value;
                        const newCurrency = newCountry === 'Canada' ? 'CAD' : 'USD';
                        setBusinessSettingsForm({...businessSettingsForm, country: newCountry, currency: newCurrency});
                      }}
                      required
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    >
                      <option value="Canada">Canada</option>
                      <option value="United States">United States</option>
                    </select>
                    <p style={{color: '#6b7280', fontSize: '0.8rem', margin: '0.25rem 0 0'}}>
                      Currency auto-set to {businessSettingsForm.country === 'Canada' ? 'CAD' : 'USD'}
                    </p>
                  </div>
                  <div>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>Fiscal Year End *</label>
                    <input
                      type="date"
                      value={businessSettingsForm.fiscal_year_end}
                      onChange={(e) => setBusinessSettingsForm({...businessSettingsForm, fiscal_year_end: e.target.value})}
                      required
                      style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px'}}
                    />
                  </div>
                </div>

                <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem'}}>
                  <button
                    type="button"
                    onClick={() => setActiveView('dashboard')}
                    style={{padding: '0.75rem 1.5rem', border: '1px solid #d1d5db', background: 'white', borderRadius: '6px', cursor: 'pointer'}}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Save Settings
                  </button>
                </div>
              </form>
            </div>
          </section>
        )}

        {activeView === 'dashboard' && !dashboardData && (
          <div className="no-data">
            <p>No dashboard data available</p>
          </div>
        )}
      </div>

      {/* Account Creation Modal */}
      {showAccountCreation && (
        <div className="modal-overlay" onClick={() => setShowAccountCreation(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Create New Account</h3>
              <button
                onClick={() => setShowAccountCreation(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: '#6b7280'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{padding: '1.5rem'}}>
              <form onSubmit={handleCreateAccount}>
                {/* Template Selection */}
                {templatesLoading ? (
                  <div style={{textAlign: 'center', padding: '2rem'}}>
                    <div className="spinner" style={{margin: '0 auto 1rem'}}></div>
                    <p>Loading account templates...</p>
                  </div>
                ) : accountTemplates && Object.keys(accountTemplates).length > 0 ? (
                  <div style={{marginBottom: '2rem'}}>
                    <h4 style={{marginBottom: '1rem'}}>📋 Choose from Templates</h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '0.75rem',
                      marginBottom: '1rem',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '1rem'
                    }}>
                      {Object.entries(accountTemplates).map(([type, templates]) => (
                        <div key={type}>
                          <h5 style={{
                            textTransform: 'capitalize',
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            marginBottom: '0.5rem'
                          }}>
                            {type}
                          </h5>
                          {templates.map((template: any) => (
                            <button
                              key={template.id}
                              type="button"
                              onClick={() => handleTemplateSelect(template)}
                              style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: '0.5rem',
                                border: accountForm.selectedTemplate?.id === template.id ? '2px solid #dc2626' : '1px solid #e5e7eb',
                                borderRadius: '4px',
                                background: accountForm.selectedTemplate?.id === template.id ? '#fef2f2' : 'white',
                                cursor: 'pointer',
                                marginBottom: '0.25rem',
                                fontSize: '0.875rem'
                              }}
                            >
                              {template.template_name}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                    
                    {accountForm.selectedTemplate && (
                      <div style={{
                        background: '#f0f9ff',
                        border: '1px solid #0ea5e9',
                        borderRadius: '6px',
                        padding: '0.75rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{color: '#0369a1'}}>
                          ✓ Selected: {accountForm.selectedTemplate.template_name}
                        </span>
                        <button
                          type="button"
                          onClick={() => setAccountForm({
                            ...accountForm,
                            selectedTemplate: null,
                            account_name: '',
                            account_type: 'asset',
                            account_number: '',
                            description: ''
                          })}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0369a1',
                            cursor: 'pointer',
                            padding: '0.25rem'
                          }}
                        >
                          Clear Selection
                        </button>
                      </div>
                    )}
                    
                    <div style={{textAlign: 'center', margin: '1rem 0'}}>
                      <span style={{color: '#6b7280'}}>— OR —</span>
                    </div>
                  </div>
                ) : null}

                {/* Custom Account Form */}
                <div>
                  <h4 style={{marginBottom: '1rem'}}>🖊️ Create Custom Account</h4>
                  
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                    <div>
                      <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>
                        Account Name *
                      </label>
                      <input
                        type="text"
                        value={accountForm.account_name}
                        onChange={(e) => setAccountForm({...accountForm, account_name: e.target.value})}
                        placeholder="e.g., Cash in Bank"
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>
                        Account Type *
                      </label>
                      <select
                        value={accountForm.account_type}
                        onChange={(e) => handleAccountTypeChange(e.target.value)}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px'
                        }}
                      >
                        <option value="asset">💰 Asset</option>
                        <option value="liability">📋 Liability</option>
                        <option value="equity">🏦 Equity</option>
                        <option value="revenue">📈 Revenue</option>
                        <option value="expense">💸 Expense</option>
                      </select>
                    </div>
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                    <div>
                      <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>
                        Account Number
                        <button
                          type="button"
                          onClick={() => {
                            const suggestedNumber = getNextAccountNumber(accountForm.account_type);
                            setAccountForm({...accountForm, account_number: suggestedNumber});
                          }}
                          style={{
                            marginLeft: '0.5rem',
                            background: 'none',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            textDecoration: 'underline'
                          }}
                        >
                          ⚡ Auto-suggest
                        </button>
                      </label>
                      <input
                        type="number"
                        value={accountForm.account_number}
                        onChange={(e) => setAccountForm({...accountForm, account_number: e.target.value})}
                        placeholder="e.g., 1001"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px'
                        }}
                      />
                      {accountForm.account_type && (
                        <div style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>
                          💡 {accountForm.account_type.charAt(0).toUpperCase() + accountForm.account_type.slice(1)} accounts typically use {
                            accountForm.account_type === 'asset' ? '1000-1999' :
                            accountForm.account_type === 'liability' ? '2000-2999' :
                            accountForm.account_type === 'equity' ? '3000-3999' :
                            accountForm.account_type === 'revenue' ? '4000-4999' :
                            '5000-5999'
                          } range
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>
                        Opening Balance
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={accountForm.opening_balance}
                        onChange={(e) => setAccountForm({...accountForm, opening_balance: parseFloat(e.target.value) || 0})}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>
                      Description
                    </label>
                    <textarea
                      value={accountForm.description}
                      onChange={(e) => setAccountForm({...accountForm, description: e.target.value})}
                      placeholder="Optional description for this account"
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </div>

                <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
                  <button
                    type="button"
                    onClick={() => setShowAccountCreation(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: '1px solid #d1d5db',
                      background: 'white',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!accountForm.account_name.trim()}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: accountForm.account_name.trim() ? '#10b981' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: accountForm.account_name.trim() ? 'pointer' : 'not-allowed'
                    }}
                  >
                    Create Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* User Invitation Modal */}
      {showUserInvitation && (
        <div className="modal-overlay" onClick={() => setShowUserInvitation(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>👥 Invite Team Members</h3>
              <button
                onClick={() => setShowUserInvitation(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: '#6b7280'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{padding: '1.5rem'}}>
              {/* Current Team Members */}
              <div style={{marginBottom: '2rem'}}>
                <h4 style={{marginBottom: '1rem'}}>👥 Current Team Members</h4>
                
                {usersLoading ? (
                  <div style={{textAlign: 'center', padding: '1rem'}}>
                    <div className="spinner" style={{margin: '0 auto 0.5rem'}}></div>
                    <p style={{color: '#6b7280', fontSize: '0.875rem'}}>Loading team members...</p>
                  </div>
                ) : businessUsers.length > 0 ? (
                  <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {businessUsers.map((user: any, index: number) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem 1rem',
                        borderBottom: index < businessUsers.length - 1 ? '1px solid #f3f4f6' : 'none'
                      }}>
                        <div>
                          <div style={{fontWeight: '500'}}>{user.user?.email || user.email}</div>
                          <div style={{fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize'}}>
                            {user.role} • {user.status}
                          </div>
                        </div>
                        {user.status === 'pending' && (
                          <span style={{
                            background: '#fef3c7',
                            color: '#d97706',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                          }}>
                            Pending
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#6b7280',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    background: '#f9fafb'
                  }}>
                    <p>No team members invited yet</p>
                    <p style={{fontSize: '0.875rem'}}>Send your first invitation below!</p>
                  </div>
                )}
              </div>

              {/* Invitation Form */}
              <div>
                <h4 style={{marginBottom: '1rem'}}>✉️ Send New Invitation</h4>
                
                <form onSubmit={handleSendInvitation}>
                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={invitationForm.email}
                      onChange={(e) => setInvitationForm({...invitationForm, email: e.target.value})}
                      placeholder="colleague@company.com"
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px'
                      }}
                    />
                  </div>

                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>
                      Role *
                    </label>
                    <select
                      value={invitationForm.role}
                      onChange={(e) => setInvitationForm({...invitationForm, role: e.target.value as any})}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px'
                      }}
                    >
                      <option value="user">👤 User (View & Create Entries)</option>
                      <option value="admin">🔑 Admin (Full Access)</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem'}}>
                      Personal Message (Optional)
                    </label>
                    <textarea
                      value={invitationForm.message}
                      onChange={(e) => setInvitationForm({...invitationForm, message: e.target.value})}
                      placeholder="Welcome to our accounting team! Looking forward to working together."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div style={{
                    background: '#f0f9ff',
                    border: '1px solid #0ea5e9',
                    borderRadius: '6px',
                    padding: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{display: 'flex', alignItems: 'flex-start', gap: '0.5rem'}}>
                      <span style={{color: '#0369a1', fontSize: '1rem'}}>💡</span>
                      <div>
                        <div style={{fontWeight: '500', color: '#0369a1', marginBottom: '0.25rem'}}>
                          Supporters Club Feature
                        </div>
                        <div style={{fontSize: '0.875rem', color: '#0369a1'}}>
                          Team collaboration is included in the Supporters Club (up to 10 users per business).
                          Invited users will have access to all accounting features.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
                    <button
                      type="button"
                      onClick={() => setShowUserInvitation(false)}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: '1px solid #d1d5db',
                        background: 'white',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!invitationForm.email.trim()}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: invitationForm.email.trim() ? '#10b981' : '#9ca3af',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: invitationForm.email.trim() ? 'pointer' : 'not-allowed'
                      }}
                    >
                      📧 Send Invitation
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BusinessDashboard;